import React, { useState, useRef, useEffect, useCallback, useMemo, startTransition } from 'react';
import './ImageGenView.css';
import { getApiBase, fetchWithTimeout, ariaUserIdJsonBody } from './apiBase';
import { sfx } from './ariaSoundFx';

const API_BASE = getApiBase();
const FETCH_LIST_MS = 15000;  // no request can hang longer — prevents "Page Unresponsive"

/** Build full URL for a video path (e.g. /videos/veo_123.mp4 or veo_123.mp4). Uses backend so /videos/ always loads. */
function fullVideoUrl(path) {
  if (!path) return null;
  const clean = String(path).replace(/^\/videos\//, '').trim();
  if (!clean) return null;
  const relative = path.startsWith('/') ? path : `/videos/${clean}`;
  let base = (API_BASE || '').replace(/\/$/, '');
  if (!base && typeof window !== 'undefined' && window.location?.origin) {
    const o = window.location.origin;
    if (/^https?:\/\//i.test(o)) base = o.replace(/\/$/, '');
  }
  if (!base && typeof import.meta !== 'undefined' && import.meta.env?.DEV) {
    base = 'http://127.0.0.1:3001';
  }
  return base ? `${base}${relative.startsWith('/') ? relative : '/' + relative}` : relative;
}

/**
 * GlassSelect — custom dropdown that looks like the rest of the liquid-glass
 * UI. Replaces the native <select> so the OPENED menu can also be glass-styled
 * (native option menus inherit the OS theme and can't be CSS-styled).
 *
 * Props: { value, onChange, options: [{value, label}], disabled, ariaLabel }
 */
function GlassSelect({ value, onChange, options, disabled, ariaLabel, className = '' }) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  const selected = options.find((o) => o.value === value);
  // Options that carry meta (quality + price + emoji) get a richer 3-column row:
  //   ★ quality   |   emoji + model name   |   price/unit
  const hasMeta = options.some((o) => o.meta);

  // Close on outside click / Escape.
  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div className={`glass-select ${open ? 'glass-select--open' : ''} ${hasMeta ? 'glass-select--has-meta' : ''} ${className}`} ref={wrapRef}>
      <button
        type="button"
        className="glass-select__trigger"
        onClick={() => {
          if (disabled) return;
          // Cool interface click tone — the user's pick for opening dropdowns.
          sfx.open();
          setOpen((v) => !v);
        }}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={ariaLabel}
      >
        {selected?.meta ? (
          // Same 3-column layout as the menu rows so the trigger and the
          // selected option line up pixel-perfect: ★ score | emoji + name | price.
          <>
            <span className="glass-select__option-quality">
              <span className="glass-select__option-star" aria-hidden="true">★</span>
              <span>{selected.meta.quality}</span>
            </span>
            <span className="glass-select__option-name">
              {selected.meta.emoji && (
                <span className="glass-select__option-emoji" aria-hidden="true">{selected.meta.emoji}</span>
              )}
              <span className="glass-select__option-name-text">{selected.label}</span>
            </span>
            <span className="glass-select__option-price">{selected.meta.price}</span>
          </>
        ) : (
          <span className="glass-select__label">{selected?.label || ''}</span>
        )}
      </button>
      {open && (
        <div className="glass-select__menu" role="listbox">
          {options.map((opt) => (
            <button
              key={opt.value}
              type="button"
              role="option"
              aria-selected={opt.value === value}
              className={`glass-select__option ${opt.value === value ? 'glass-select__option--active' : ''} ${opt.meta ? 'glass-select__option--has-meta' : ''}`}
              onClick={() => { sfx.click(); onChange(opt.value); setOpen(false); }}
            >
              {opt.meta ? (
                <>
                  {/* LEFT: quality score with ★ on the left of the number. */}
                  <span className="glass-select__option-quality">
                    <span className="glass-select__option-star" aria-hidden="true">★</span>
                    <span>{opt.meta.quality}</span>
                  </span>
                  {/* MIDDLE: brand emoji + model name. */}
                  <span className="glass-select__option-name">
                    {opt.meta.emoji && (
                      <span className="glass-select__option-emoji" aria-hidden="true">{opt.meta.emoji}</span>
                    )}
                    <span className="glass-select__option-name-text">{opt.label}</span>
                  </span>
                  {/* FAR RIGHT: price per unit. */}
                  <span className="glass-select__option-price">{opt.meta.price}</span>
                </>
              ) : (
                <span className="glass-select__option-label">{opt.label}</span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/** Wraps a video so it plays with sound when the user hovers (website + Mac app). Tries unmuted first; falls back to muted if the browser blocks. */
function HoverVideo({ className, src, ...props }) {
  const videoRef = useRef(null);
  const [loadError, setLoadError] = useState(false);
  const onEnter = useCallback(() => {
    const el = videoRef.current;
    if (el && !loadError) {
      el.muted = false;
      el.play().catch(() => {});
    }
  }, [loadError]);
  const onLeave = useCallback(() => {
    const el = videoRef.current;
    if (el) {
      el.pause();
      el.currentTime = 0;
    }
  }, []);
  if (!src) return null;
  return (
    <div className="image-gen-hover-video-wrap" onMouseEnter={onEnter} onMouseLeave={onLeave}>
      {loadError ? (
        <div className="image-gen-video-error">
          <p>Video couldn’t load.</p>
          <p className="image-gen-video-error-hint">Run the backend: <code>cd HowTo-Backend && npm start</code></p>
        </div>
      ) : (
        <video
          ref={videoRef}
          src={src}
          className={className}
          playsInline
          preload="metadata"
          onError={() => setLoadError(true)}
          {...props}
        />
      )}
    </div>
  );
}

const MODE_PICTURE = 'picture';
const MODE_VIDEO = 'video';
const MODE_AUDIO = 'audio';
const VIDEO_IMAGE_MODE_REF = 'reference';      // up to 3 character reference images
const VIDEO_IMAGE_MODE_START_END = 'startEnd'; // start frame & end frame (Veo allows one or the other, not both)

const VEO_MODEL_FAST = 'veo-3.1-fast-generate-preview';
const VEO_MODEL_PRO = 'veo-3.1-generate-preview';
const VEO_DURATIONS = [4, 6, 8];
const VEO_RESOLUTIONS = ['720p', '1080p'];

// Picture-mode (Nano Banana) tiers + reference cap.
const NANO_TIER_PRO = 'pro';
const NANO_TIER_FAST = 'fast';
const MAX_PICTURE_REFS = 10;

// Audio-mode (ElevenLabs) tiers + durations.
const AUDIO_MODEL_STUDIO = 'elevenlabs-studio';
const AUDIO_MODEL_FAST = 'elevenlabs-fast';
const AUDIO_DURATIONS = [10, 20, 30, 60];

/**
 * Per-model quality score (0–100) + per-unit cost shown on every result tile.
 * Quality is a subjective rating based on Anthropic/Google published benchmarks.
 * Cost is the consumer-facing rate the user pays — image models bill per image,
 * video models bill per second of generated footage.
 */
const MODEL_INFO = {
  // ── Picture models (Nano Banana via Google Imagen) ──────────────────────
  [NANO_TIER_PRO]:  { kind: 'image', quality: 92, rate: 0.039, unit: 'image', label: 'Nano Pro',  emoji: '🍌' },
  [NANO_TIER_FAST]: { kind: 'image', quality: 74, rate: 0.020, unit: 'image', label: 'Nano Fast', emoji: '🍌' },
  // ── Video models (Veo 3.1) ──────────────────────────────────────────────
  [VEO_MODEL_FAST]: { kind: 'video', quality: 76, rate: 0.10, unit: 'sec',  label: 'Veo 3.1 Fast', emoji: '🎬' },
  [VEO_MODEL_PRO]:  { kind: 'video', quality: 98, rate: 0.40, unit: 'sec',  label: 'Veo 3.1 Pro',  emoji: '🎬' },
  // ── Audio models (ElevenLabs voice + music) ─────────────────────────────
  [AUDIO_MODEL_STUDIO]: { kind: 'audio', quality: 94, rate: 0.05, unit: 'sec', label: 'ElevenLabs Studio', emoji: '🎙️' },
  [AUDIO_MODEL_FAST]:   { kind: 'audio', quality: 78, rate: 0.02, unit: 'sec', label: 'ElevenLabs Fast',   emoji: '🎙️' },
};

/**
 * Image size options. Nano Banana Pro supports 1K / 2K / 4K natively at the
 * backend (see nanoBananaService.js). We expose only 2K + 4K — 1K is rarely
 * useful for the consumer use cases. Quality is the relative sharpness rating;
 * priceMultiplier scales the model's per-image rate (e.g. 4K ≈ 2× the cost).
 */
const SIZE_2K = '2K';
const SIZE_4K = '4K';
const SIZE_INFO = {
  [SIZE_2K]: { quality: 92, priceMultiplier: 1.0,  label: '2K',   sub: '2048×2048 · standard' },
  [SIZE_4K]: { quality: 98, priceMultiplier: 2.0,  label: '4K',   sub: '4096×4096 · ultra-sharp' },
};

/** Format a rate as a price label, e.g. 0.04 → "$0.04/image". */
function priceLabel(rate, unit) {
  if (!Number.isFinite(rate)) return '';
  return `$${rate.toFixed(2)}/${unit}`;
}

const IMAGEN_GEN_RESULTS_KEY = 'imageGen_generatedResults';
const IMAGEN_GEN_RESULTS_MAX = 50;
const MAX_VISIBLE_ITEMS = 20; // Cap DOM so 50 videos/images never render at once (avoids "Page Unresponsive")
// Only show images/videos the user created in Image Gen; how-to app content is used elsewhere.
// userCreatedOnly=true shows only Image Gen (veo_*) videos. finalOnly=false so newly generated raw Veo videos show in the library immediately (before add-audio).
const USER_CREATED_QUERY = 'userCreatedOnly=true';
const FINAL_ONLY_QUERY = 'finalOnly=false';
const CHARACTER_REF_SLOTS = 3; // Veo supports up to 3 reference images for same person
const PERSIST_MAX_BYTES = 2 * 1024 * 1024; // 2MB cap so we never block main thread

function loadPersistedResults() {
  try {
    const raw = typeof localStorage !== 'undefined' ? localStorage.getItem(IMAGEN_GEN_RESULTS_KEY) : null;
    if (!raw || raw.length > PERSIST_MAX_BYTES) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.slice(0, IMAGEN_GEN_RESULTS_MAX) : [];
  } catch {
    return [];
  }
}

function readFileAsBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result;
      const match = dataUrl.match(/^data:([^;]+);base64,(.+)$/);
      if (match) resolve({ mimeType: match[1], dataBase64: match[2] });
      else reject(new Error('Invalid file'));
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

function ImageGenView() {
  const [prompt, setPrompt] = useState('');
  const promptRef = useRef(null); // so the edit-from-result button can scroll + focus the textarea
  const [dialogText, setDialogText] = useState(''); // Exact words for TTS (optional; if empty, backend uses text in quotes from prompt)
  const [mode, setMode] = useState(MODE_VIDEO);
  const [refCharacter, setRefCharacter] = useState(null);
  const [characterRefs, setCharacterRefs] = useState(() => Array(CHARACTER_REF_SLOTS).fill(null)); // Video: up to 3 refs
  const [refBackground, setRefBackground] = useState(null);
  const [refDialog, setRefDialog] = useState(null);
  // Picture-mode: up to 10 generic reference images + tier selection.
  const [pictureRefs, setPictureRefs] = useState(() => Array(MAX_PICTURE_REFS).fill(null));
  const [nanoTier, setNanoTier] = useState(NANO_TIER_PRO);
  const [imageSize, setImageSize] = useState(SIZE_2K);
  // Aspect ratio — visual frame-shape picker next to Pixel. Defaults to 9:16
  // (portrait, matches the previous hardcoded behavior).
  const [aspectRatio, setAspectRatio] = useState('9:16');
  // ── Monthly image spend — shown as a small badge in the top-right of
  //    the AI Gen view. Pure ambient awareness; click to expand the
  //    per-model breakdown. Refreshes after each generation.
  const [monthlyImageSpend, setMonthlyImageSpend] = useState({ total: 0, count: 0, byModel: {} });
  const [monthlyBadgeOpen, setMonthlyBadgeOpen] = useState(false);
  const [veoModel, setVeoModel] = useState(VEO_MODEL_FAST);
  const [veoDuration, setVeoDuration] = useState(4);
  const [veoResolution, setVeoResolution] = useState('720p');
  // Audio-mode state — model + clip length.
  const [audioModel, setAudioModel] = useState(AUDIO_MODEL_STUDIO);
  const [audioDuration, setAudioDuration] = useState(10);
  const [addBackgroundMusic, setAddBackgroundMusic] = useState(false);
  const [voiceMode, setVoiceMode] = useState('auto'); // 'auto' = Veo sound, 'custom' = ElevenLabs Voice Changer
  const [videoImageMode, setVideoImageMode] = useState(VIDEO_IMAGE_MODE_REF); // 'reference' | 'startEnd' — Veo allows only one
  const [startFrame, setStartFrame] = useState(null);
  const [endFrame, setEndFrame] = useState(null);
  const [generateCount, setGenerateCount] = useState(1);
  const [generatingCurrent, setGeneratingCurrent] = useState(0);
  const [generatingTotal, setGeneratingTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  // ETA countdown — set when generation starts so the user can see how long is left
  const [generationStartedAt, setGenerationStartedAt] = useState(0);
  const [generationEstSeconds, setGenerationEstSeconds] = useState(0);
  const [nowTick, setNowTick] = useState(0);
  useEffect(() => {
    if (!loading) return;
    const id = setInterval(() => setNowTick(Date.now()), 500);
    return () => clearInterval(id);
  }, [loading]);
  const [result, setResult] = useState(null);
  const [resultsList, setResultsList] = useState([]);
  const [error, setError] = useState('');
  const [elevenVoices, setElevenVoices] = useState([]);
  const [elevenLabsConfigured, setElevenLabsConfigured] = useState(null); // null = not loaded yet, true/false from API
  const [selectedVoiceId, setSelectedVoiceId] = useState('');
  const [pipelineHealth, setPipelineHealth] = useState(null); // { allReady, systems } from /api/veo/pipeline-health
  const [addingAudioForUrl, setAddingAudioForUrl] = useState(null); // videoUrl when adding audio to existing video
  const [voicePreviewPlaying, setVoicePreviewPlaying] = useState(false);
  const [lastAddedVideoUrl, setLastAddedVideoUrl] = useState(null); // show "New" on this video after Add voice & sound
  const [watchVideoUrl, setWatchVideoUrl] = useState(null); // when set, show modal with video player
  // ── Image expand-to-bubble modal ─ click any result image to view it
  //    centered with the rest of the gallery blurred behind it.
  const [viewImageUrl, setViewImageUrl] = useState(null);
  useEffect(() => {
    if (!viewImageUrl) return;
    const onKey = (e) => { if (e.key === 'Escape') setViewImageUrl(null); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [viewImageUrl]);
  // ── Create-a-voice modal (ElevenLabs Voice Designer) ─────────────
  // Two flows toggled at the top of the modal:
  //   - random: pick gender → generate 3 preview voices → pick one
  //   - clone:  record / upload audio of own voice → instant clone
  const [createVoiceOpen, setCreateVoiceOpen] = useState(false);
  const [createVoiceTab, setCreateVoiceTab] = useState('random'); // 'random' | 'clone'
  const [createVoiceName, setCreateVoiceName] = useState('');
  const [createVoiceGender, setCreateVoiceGender] = useState('woman'); // 'man' | 'woman'
  const [createVoicePreviews, setCreateVoicePreviews] = useState([]); // [{ generated_voice_id, audio_base_64, media_type, audio_url }]
  const [createVoiceSelectedIdx, setCreateVoiceSelectedIdx] = useState(-1);
  const [createVoicePlayingIdx, setCreateVoicePlayingIdx] = useState(-1);
  const [createVoiceDescription, setCreateVoiceDescription] = useState(''); // the prompt actually used (returned by backend)
  const [createVoicePending, setCreateVoicePending] = useState(false);
  const [createVoiceError, setCreateVoiceError] = useState('');
  // Clone-flow state
  const [cloneRecording, setCloneRecording] = useState(false);
  const [cloneAudioBase64, setCloneAudioBase64] = useState(''); // captured / uploaded sample
  const [cloneAudioUrl, setCloneAudioUrl] = useState(''); // for the local preview player
  const [cloneAudioMime, setCloneAudioMime] = useState('audio/webm');
  const mediaRecorderRef = useRef(null);
  const recordedChunksRef = useRef([]);
  const VIDEO_MUTED_STORAGE_KEY = 'imageGenVideosMuted';
  const [videosMuted, setVideosMutedState] = useState(() => {
    try {
      const stored = typeof localStorage !== 'undefined' ? localStorage.getItem(VIDEO_MUTED_STORAGE_KEY) : null;
      return stored === 'true';
    } catch { return false; }
  });
  const watchVideoRef = useRef(null);
  const setVideosMuted = (muted) => {
    setVideosMutedState(muted);
    try { localStorage.setItem(VIDEO_MUTED_STORAGE_KEY, String(muted)); } catch {}
  };
  useEffect(() => {
    if (!watchVideoUrl) return;
    const el = watchVideoRef.current;
    if (!el) return;
    const tryPlay = () => {
      el.muted = videosMuted;
      el.play().catch(() => {});
    };
    if (el.readyState >= 2) tryPlay();
    else el.addEventListener('canplay', tryPlay, { once: true });
    const t = setTimeout(tryPlay, 100);
    return () => {
      clearTimeout(t);
      el.removeEventListener('canplay', tryPlay);
    };
  }, [watchVideoUrl, videosMuted]);
  const [savedVideos, setSavedVideos] = useState([]); // videos from disk (GET /api/veo/saved-videos)
  const [videosWithAudio, setVideosWithAudio] = useState([]); // only videos fully made with our tailored audio (merged)
  const [mainVideoOverrideUrl, setMainVideoOverrideUrl] = useState(null); // when set, main player shows this (e.g. with stored sound)
  const [loadingStoredSound, setLoadingStoredSound] = useState(null); // filename while loading with-stored-audio
  const [storedAudioList, setStoredAudioList] = useState([]); // { videoFilename, voice, background, hasBoth, mergedVideoUrl? }[]
  const [storedVideoIndex, setStoredVideoIndex] = useState(0); // which video to show in single-video viewer
  const [mergedVideoUrlByFilename, setMergedVideoUrlByFilename] = useState({}); // after "Play with stored sound", cache merged URL so we show video+sound in list
  const [showAllResults, setShowAllResults] = useState(false); // when false, only show first MAX_VISIBLE_ITEMS
  const [resultsPerRow, setResultsPerRow] = useState(2); // 1, 2, 3, or 4 items per row for accessibility
  const [resultsPanelVisible, setResultsPanelVisible] = useState(() => {
    try { return localStorage.getItem('aria_imagegen_panel_visible') !== '0'; } catch (_) { return true; }
  });

  // Only show videos the user created (present in image-gen history)
  const videoFilenamesFromHistory = useMemo(() => {
    const set = new Set();
    const norm = (url) => (url || '').replace(/^\/videos\//, '').split('?')[0].trim();
    resultsList.forEach((item) => {
      if (item.type === 'video') {
        const path = item.data?.videoUrl || item.filename || item.data?.filename;
        if (path) set.add(norm(path));
      }
    });
    return set;
  }, [resultsList]);
  const storedVideosWithFilename = useMemo(() => {
    return storedAudioList.filter((item) => {
      if (!item.videoFilename) return false;
      const fn = String(item.videoFilename).replace(/^\/videos\//, '').split('?')[0].trim();
      return videoFilenamesFromHistory.has(fn);
    });
  }, [storedAudioList, videoFilenamesFromHistory]);
  useEffect(() => {
    if (storedVideoIndex >= storedVideosWithFilename.length && storedVideosWithFilename.length > 0) {
      setStoredVideoIndex(Math.max(0, storedVideosWithFilename.length - 1));
    }
  }, [storedVideosWithFilename.length, storedVideoIndex]);
  useEffect(() => {
    if (!watchVideoUrl) return;
    const onKey = (e) => { if (e.key === 'Escape') setWatchVideoUrl(null); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [watchVideoUrl]);
  const [refreshingLists, setRefreshingLists] = useState(false); // true while Update (refresh) is running
  const [panelInitialLoading, setPanelInitialLoading] = useState(true); // true until first history/videos load settles
  const charInputRef = useRef(null);
  const resultsListRef = useRef(null);
  const charRefInputRefs = useRef([]); // refs for character ref slots 0,1,2
  const bgInputRef = useRef(null);
  const dialogInputRef = useRef(null);
  const startFrameRef = useRef(null);
  const endFrameRef = useRef(null);
  const autoAddAudioAttemptedRef = useRef(new Set()); // URLs we've already auto-triggered add-audio for
  const [checkingImages, setCheckingImages] = useState(false);
  const [checkResult, setCheckResult] = useState(null);

  const handleRefFile = (section, file) => {
    if (!file || !file.type.startsWith('image/')) return;
    readFileAsBase64(file)
      .then((data) => {
        if (section === 'character') setRefCharacter(data);
        if (section.startsWith('characterRef')) {
          const match = section.match(/^characterRef(\d)$/);
          if (match) {
            const idx = parseInt(match[1], 10);
            if (idx >= 0 && idx < CHARACTER_REF_SLOTS) {
              setCharacterRefs((prev) => {
                const next = [...(prev.length >= CHARACTER_REF_SLOTS ? prev : [...prev, ...Array(CHARACTER_REF_SLOTS - prev.length).fill(null)])];
                next[idx] = data;
                return next;
              });
            }
          }
        }
        if (section === 'background') setRefBackground(data);
        if (section === 'dialog') setRefDialog(data);
        if (section === 'startFrame') setStartFrame(data);
        if (section === 'endFrame') setEndFrame(data);
        if (section.startsWith('pictureRef')) {
          const m = section.match(/^pictureRef(\d+)$/);
          if (m) {
            const idx = parseInt(m[1], 10);
            if (idx >= 0 && idx < MAX_PICTURE_REFS) {
              setPictureRefs((prev) => {
                const next = [...prev];
                next[idx] = data;
                return next;
              });
            }
          }
        }
        // Arcade jump coin — celebrates a successful image upload.
        sfx.coin();
      })
      .catch(() => { setError('Could not read image file'); sfx.error(); });
  };

  const handleDrop = (section, e) => {
    e.preventDefault();
    e.stopPropagation();
    const file = e.dataTransfer?.files?.[0];
    if (file) handleRefFile(section, file);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const removeRef = (section) => {
    if (section === 'character') setRefCharacter(null);
    if (section.startsWith('characterRef')) {
      const match = section.match(/^characterRef(\d)$/);
      if (match) {
        const idx = parseInt(match[1], 10);
        if (idx >= 0 && idx < CHARACTER_REF_SLOTS) {
          setCharacterRefs((prev) => {
            const next = [...prev];
            next[idx] = null;
            return next;
          });
        }
      }
    }
    if (section === 'background') setRefBackground(null);
    if (section === 'dialog') setRefDialog(null);
    if (section === 'startFrame') setStartFrame(null);
    if (section === 'endFrame') setEndFrame(null);
    if (section.startsWith('pictureRef')) {
      const m = section.match(/^pictureRef(\d+)$/);
      if (m) {
        const idx = parseInt(m[1], 10);
        if (idx >= 0 && idx < MAX_PICTURE_REFS) {
          setPictureRefs((prev) => {
            const next = [...prev];
            next[idx] = null;
            return next;
          });
        }
      }
    }
  };

  useEffect(() => {
    if (mode !== MODE_VIDEO) return;
    const ac = new AbortController();
    const base = API_BASE || "";
    fetchWithTimeout(`${base}/api/elevenlabs/voices`, { signal: ac.signal, timeout: FETCH_LIST_MS })
      .then((r) => (r.ok ? r.json() : Promise.resolve({ configured: false, voices: [] })))
      .then((data) => {
        setElevenLabsConfigured(!!(data && data.configured));
        if (data && data.voices && data.voices.length) {
          setElevenVoices(data.voices);
          if (!selectedVoiceId && data.voices[0]) setSelectedVoiceId(data.voices[0].voice_id);
        } else {
          setElevenVoices([]);
        }
      })
      .catch(() => { setElevenLabsConfigured(false); setElevenVoices([]); });
    return () => ac.abort();
  }, [mode]);

  // Saved videos from disk – load first so the list shows quickly
  useEffect(() => {
    if (mode !== MODE_VIDEO) return;
    const ac = new AbortController();
    const base = API_BASE || "";
    fetchWithTimeout(`${base}/api/veo/saved-videos?${USER_CREATED_QUERY}&${FINAL_ONLY_QUERY}`, { signal: ac.signal, timeout: FETCH_LIST_MS })
      .then((r) => r.json())
      .then((data) => {
        if (data.ok && Array.isArray(data.videos) && data.videos.length > 0) {
          setSavedVideos(data.videos);
        } else {
          setSavedVideos([]);
        }
      })
      .catch(() => setSavedVideos([]));
    return () => ac.abort();
  }, [mode]);

  // Fetch pipeline-health and videos-with-audio when in video mode (stored-audio already fetched on initial load)
  useEffect(() => {
    if (mode !== MODE_VIDEO) return;
    const ac = new AbortController();
    const base = API_BASE || "";
    fetchWithTimeout(`${base}/api/veo/pipeline-health`, { signal: ac.signal, timeout: FETCH_LIST_MS })
      .then((r) => r.json())
      .then((data) => { if (data && data.systems) setPipelineHealth(data); })
      .catch(() => setPipelineHealth(null));
    fetchWithTimeout(`${base}/api/veo/videos-with-audio?${USER_CREATED_QUERY}&${FINAL_ONLY_QUERY}`, { signal: ac.signal, timeout: FETCH_LIST_MS })
      .then((r) => r.json())
      .then((data) => {
        if (data.ok && Array.isArray(data.videos)) setVideosWithAudio(data.videos);
        else setVideosWithAudio([]);
      })
      .catch(() => setVideosWithAudio([]));
    return () => ac.abort();
  }, [mode]);

  // Refresh lists (saved videos, stored audio, videos-with-audio, history) – call when user clicks Update
  const refreshVideoAndResultsLists = useCallback(() => {
    if (mode !== MODE_VIDEO) return;
    setRefreshingLists(true);
    const base = API_BASE || "";
    const opts = { timeout: FETCH_LIST_MS };
    Promise.all([
      fetchWithTimeout(`${base}/api/veo/saved-videos?${USER_CREATED_QUERY}&${FINAL_ONLY_QUERY}`, opts).then((r) => r.json()),
      fetchWithTimeout(`${base}/api/veo/stored-audio`, opts).then((r) => r.json()),
      fetchWithTimeout(`${base}/api/veo/videos-with-audio?${USER_CREATED_QUERY}&${FINAL_ONLY_QUERY}`, opts).then((r) => r.json()),
      fetchWithTimeout(`${base}/api/image-gen/history?limit=${IMAGEN_GEN_RESULTS_MAX}`, opts).then((r) => r.json()),
    ])
      .then(([saved, stored, withAudio, history]) => {
        if (saved?.ok && Array.isArray(saved.videos) && saved.videos.length > 0) {
          setSavedVideos(saved.videos);
        } else {
          setSavedVideos([]);
        }
        if (stored?.ok && Array.isArray(stored.storedAudio)) setStoredAudioList(stored.storedAudio);
        else setStoredAudioList([]);
        if (withAudio?.ok && Array.isArray(withAudio.videos)) setVideosWithAudio(withAudio.videos);
        else setVideosWithAudio([]);
        if (history?.ok && Array.isArray(history.items) && history.items.length > 0) {
          startTransition(() => {
            setResultsList(history.items);
            setShowAllResults(false);
          });
        }
      })
      .catch(() => {})
      .finally(() => {
        setRefreshingLists(false);
        setPanelInitialLoading(false);
      });
  }, [mode]);

  // Load persisted results after first paint so we never block the main thread (avoids "Page Unresponsive").
  useEffect(() => {
    const id = setTimeout(() => {
      requestAnimationFrame(() => {
        try {
          const loaded = loadPersistedResults();
          if (loaded.length > 0) startTransition(() => setResultsList(loaded));
        } catch (_) {}
      });
    }, 0);
    return () => clearTimeout(id);
  }, []);

  // Load history and videos on mount. Never block render: always clear loading after timeout or when done.
  // Monthly image spend — fetch on mount, refresh every 60s + after each
  // generation. Ambient cost transparency, no per-click anxiety.
  useEffect(() => {
    let cancelled = false;
    const fetchMonthly = () => {
      fetch(`${API_BASE}/api/usage/monthly-images`)
        .then((r) => r.json())
        .then((data) => {
          if (cancelled || !data?.ok) return;
          setMonthlyImageSpend({
            total: data.total || 0,
            count: data.count || 0,
            byModel: data.byModel || {},
          });
        })
        .catch(() => { /* silent — badge just stays at last known value */ });
    };
    fetchMonthly();
    const iv = setInterval(fetchMonthly, 60_000);
    // Listen for a "results refreshed" cue from elsewhere in the view, so the
    // badge ticks up immediately after a successful generation.
    const onRefresh = () => fetchMonthly();
    window.addEventListener('imagegen:results-changed', onRefresh);
    return () => {
      cancelled = true;
      clearInterval(iv);
      window.removeEventListener('imagegen:results-changed', onRefresh);
    };
  }, []);

  useEffect(() => {
    const base = API_BASE || "";
    let cancelled = false;
    const ac = new AbortController();
    const done = () => {
      if (!cancelled) {
        cancelled = true;
        setPanelInitialLoading(false);
      }
    };
    const timeoutId = setTimeout(done, 8000); // show content after 8s even if backend is slow or down
    const opts = { signal: ac.signal, timeout: FETCH_LIST_MS };

    try {
      Promise.all([
        fetchWithTimeout(`${base}/api/image-gen/history?limit=${IMAGEN_GEN_RESULTS_MAX}`, opts).then((r) => r.json()).catch(() => ({ ok: false, items: [] })),
        fetchWithTimeout(`${base}/api/veo/videos-quick?${USER_CREATED_QUERY}&${FINAL_ONLY_QUERY}`, opts).then((r) => r.json()).catch(() => ({ ok: false, videos: [] })),
      ])
        .then(([history, quick]) => {
          if (cancelled) return;
          if (history?.ok && Array.isArray(history.items) && history.items.length > 0) {
            startTransition(() => {
              setResultsList(history.items);
              setShowAllResults(false);
            });
          }
          if (quick?.ok && Array.isArray(quick.videos) && quick.videos.length > 0) {
            setStoredAudioList(quick.videos.map((v) => ({ videoFilename: v.videoFilename, voice: null, background: null, mergedVideoUrl: null })));
          }
        })
        .catch(() => {})
        .finally(() => {
          clearTimeout(timeoutId);
          done();
        });

      fetchWithTimeout(`${base}/api/veo/stored-audio`, opts)
        .then((r) => r.json())
        .then((data) => {
          if (cancelled || !data?.ok || !Array.isArray(data.storedAudio)) return;
          const stored = data.storedAudio.filter((a) => a.videoFilename);
          if (stored.length === 0) return;
          setStoredAudioList((prev) => {
            if (prev.length === 0) return stored;
            const byFilename = new Map(prev.map((p) => [p.videoFilename, { ...p }]));
            stored.forEach((a) => {
              byFilename.set(a.videoFilename, { ...byFilename.get(a.videoFilename), ...a });
            });
            return Array.from(byFilename.values());
          });
        })
        .catch(() => {});
    } catch (_) {
      done();
    }
    return () => {
      cancelled = true;
      ac.abort();
      clearTimeout(timeoutId);
    };
  }, []);

  // Single list of user-created items (like image gen right panel): history + tailored-audio videos
  const allGeneratedItems = useMemo(() => {
    const seen = new Set();
    const norm = (url) => (url || '').replace(/^\/videos\//, '').split('?')[0].trim();
    const out = [];
    resultsList.forEach((item) => {
      if (item.type === 'video' && item.data?.videoUrl) seen.add(norm(item.data.videoUrl));
      out.push(item);
    });
    if (mode === MODE_VIDEO && videosWithAudio.length > 0) {
      videosWithAudio.forEach((v) => {
        const path = (v.videoUrl || '').replace(/^\/videos\//, '').split('?')[0].trim();
        if (path && !seen.has(path)) {
          seen.add(path);
          out.push({ type: 'video', data: { videoUrl: v.videoUrl, hasAudio: true }, filename: v.filename });
        }
      });
    }
    out.sort((a, b) => {
      const aHasAudio = a.type === 'video' && a.data?.hasAudio;
      const bHasAudio = b.type === 'video' && b.data?.hasAudio;
      if (aHasAudio && !bHasAudio) return -1;
      if (!aHasAudio && bHasAudio) return 1;
      return 0;
    });
    return out;
  }, [resultsList, mode, videosWithAudio]);

  const visibleItems = showAllResults ? allGeneratedItems : allGeneratedItems.slice(0, MAX_VISIBLE_ITEMS);
  const hasMoreItems = allGeneratedItems.length > MAX_VISIBLE_ITEMS;

  // Persist generated videos/images to localStorage (deferred so it never blocks the main thread).
  useEffect(() => {
    if (resultsList.length === 0) return;
    const id = setTimeout(() => {
      try {
        const toStore = resultsList.slice(0, IMAGEN_GEN_RESULTS_MAX);
        const raw = JSON.stringify(toStore);
        if (raw.length > PERSIST_MAX_BYTES) return;
        localStorage.setItem(IMAGEN_GEN_RESULTS_KEY, raw);
      } catch (_) {}
    }, 100);
    return () => clearTimeout(id);
  }, [resultsList]);

  // Add voice is manual only (click "Add voice & sound") — no auto-run on load to keep the page responsive.

  const ADD_AUDIO_TIMEOUT_MS = 120000; // 2 min – scan + ElevenLabs + merge can be slow

  const handleAddAudio = async (videoUrl, options = {}) => {
    if (!videoUrl || addingAudioForUrl) return;
    setAddingAudioForUrl(videoUrl);
    setError('');
    const urlToSend = videoUrl.startsWith('/') ? videoUrl : `/videos/${videoUrl}`;
    const payload = { videoUrl: urlToSend };
    if (dialogText.trim()) payload.dialogText = dialogText.trim();
    // In "Auto (Veo sound)" we do not want ElevenLabs to generate/merge anything.
    payload.addBackgroundMusic = voiceMode === 'custom' ? addBackgroundMusic : false;
    payload.voiceMode = voiceMode;
    if (voiceMode === 'auto') {
      payload.useVeoSoundOnly = true;
    } else {
      payload.useVoiceChanger = true;
      if (selectedVoiceId) payload.voiceId = selectedVoiceId;
    }
    try {
      const res = await fetchWithTimeout(`${API_BASE}/api/veo/add-audio`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        timeout: ADD_AUDIO_TIMEOUT_MS,
      });
      const data = await res.json();
      if (!res.ok) {
        const msg = data.message || data.error || 'Add audio failed';
        if (res.status === 429 || data.error === 'ELEVENLABS_BUSY') {
          throw new Error('Voice is being added to another video (one at a time). Wait for it to finish, then click "Add voice & sound" again.');
        }
        if (res.status === 401 || (data.error === 'ELEVENLABS_UNAUTHORIZED')) {
          throw new Error('ElevenLabs API key invalid or expired. Add a valid ELEVENLABS_API_KEY to HowTo-Backend/.env (get key at elevenlabs.io), then restart the backend.');
        }
        throw new Error(msg);
      }
      const next = { type: 'video', data: { ...data, videoUrl: data.videoUrl, hasAudio: true } };
      setResultsList((prev) => [next, ...prev]);
      setResult(next);
      setLastAddedVideoUrl(data.videoUrl || null);
      setTimeout(() => { setLastAddedVideoUrl(null); }, 8000);
      setTimeout(() => {
        resultsListRef.current?.querySelector('.image-gen-results-item')?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }, 100);
    } catch (e) {
      if (e.name === 'AbortError') {
        setError('Add audio timed out (scan + voice can take 1–2 min). Click "Add voice & sound" to try again.');
        autoAddAudioAttemptedRef.current.delete(urlToSend);
      } else {
        setError(e.message || 'Could not add audio');
      }
    } finally {
      setAddingAudioForUrl(null);
    }
  };

  const playWithStoredSound = async (filename) => {
    if (!filename || loadingStoredSound) return;
    setLoadingStoredSound(filename);
    setError('');
    try {
      const res = await fetchWithTimeout(`${API_BASE}/api/veo/with-stored-audio/${encodeURIComponent(filename)}`, { timeout: FETCH_LIST_MS });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || data.error || 'Could not load video with stored sound');
      const fullUrl = (data.videoUrl || '').startsWith('/') ? API_BASE.replace(/\/$/, '') + data.videoUrl : data.videoUrl;
      setMainVideoOverrideUrl(fullUrl);
      setWatchVideoUrl(fullUrl);
      setMergedVideoUrlByFilename((prev) => ({ ...prev, [filename]: fullUrl }));
    } catch (e) {
      setError(e.message || 'Could not load video with stored sound');
    } finally {
      setLoadingStoredSound(null);
    }
  };

  const playVoicePreview = async () => {
    if (!selectedVoiceId) return;
    setVoicePreviewPlaying(true);
    setError('');
    const url = `${API_BASE.replace(/\/$/, '')}/api/elevenlabs/voice-preview/${encodeURIComponent(selectedVoiceId)}`;
    try {
      const res = await fetchWithTimeout(url, { timeout: FETCH_LIST_MS });
      if (!res.ok) {
        const msg = await res.text();
        throw new Error(res.status === 400 ? 'Preview not available (check ELEVENLABS_API_KEY)' : msg || 'Preview failed');
      }
      const blob = await res.blob();
      const blobUrl = URL.createObjectURL(blob);
      const audio = new Audio(blobUrl);
      audio.onended = () => {
        URL.revokeObjectURL(blobUrl);
        setVoicePreviewPlaying(false);
      };
      audio.onerror = () => {
        URL.revokeObjectURL(blobUrl);
        setVoicePreviewPlaying(false);
      };
      try {
        await audio.play();
      } catch (playErr) {
        URL.revokeObjectURL(blobUrl);
        throw playErr;
      }
    } catch (e) {
      console.error('Voice preview error:', e);
      setError(e.message || 'Could not play voice sample');
      setVoicePreviewPlaying(false);
    }
  };

  /**
   * Reset all Create-a-voice modal state — used when opening/closing or
   * switching tabs so the user doesn't carry stale previews around.
   */
  const resetCreateVoiceFlow = () => {
    setCreateVoicePreviews([]);
    setCreateVoiceSelectedIdx(-1);
    setCreateVoicePlayingIdx(-1);
    setCreateVoiceDescription('');
    setCreateVoiceError('');
    setCloneAudioBase64('');
    setCloneAudioUrl('');
    setCloneAudioMime('audio/webm');
  };

  /**
   * Random flow — Step 1: fetch 3 voice previews from the backend based
   * on the selected gender. User picks one to save.
   */
  const handleGenerateVoicePreviews = async () => {
    setCreateVoiceError('');
    setCreateVoicePreviews([]);
    setCreateVoiceSelectedIdx(-1);
    setCreateVoicePending(true);
    try {
      const base = API_BASE.replace(/\/$/, '');
      const res = await fetchWithTimeout(`${base}/api/elevenlabs/voice-previews`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ gender: createVoiceGender }),
        timeout: 60000,
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.ok) {
        throw new Error(data.message || data.error || `Preview generation failed (${res.status})`);
      }
      // Decode each base64 audio into a Blob URL we can pop into <audio>.
      const previewsWithUrls = (data.previews || []).map((p) => {
        try {
          const bytes = Uint8Array.from(atob(p.audio_base_64), (c) => c.charCodeAt(0));
          const blob = new Blob([bytes], { type: p.media_type || 'audio/mp3' });
          return { ...p, audio_url: URL.createObjectURL(blob) };
        } catch {
          return { ...p, audio_url: '' };
        }
      });
      setCreateVoicePreviews(previewsWithUrls);
      setCreateVoiceDescription(data.description || '');
      sfx.tap();
    } catch (err) {
      console.error('Generate previews error:', err);
      setCreateVoiceError(err.message || 'Could not generate voice previews');
    } finally {
      setCreateVoicePending(false);
    }
  };

  /**
   * Random flow — Step 2: save the chosen preview as a real voice with
   * a permanent voice_id, refresh the voices list, auto-select the new one.
   */
  const handleSaveSelectedPreview = async () => {
    const name = createVoiceName.trim();
    if (!name) { setCreateVoiceError('Give your voice a name.'); return; }
    if (createVoiceSelectedIdx < 0 || !createVoicePreviews[createVoiceSelectedIdx]) {
      setCreateVoiceError('Pick a preview first.');
      return;
    }
    const chosen = createVoicePreviews[createVoiceSelectedIdx];
    setCreateVoicePending(true);
    setCreateVoiceError('');
    try {
      const base = API_BASE.replace(/\/$/, '');
      const res = await fetchWithTimeout(`${base}/api/elevenlabs/save-voice-preview`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name,
          generated_voice_id: chosen.generated_voice_id,
          description: createVoiceDescription || `A ${createVoiceGender} voice`,
        }),
        timeout: 60000,
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.ok) {
        throw new Error(data.message || data.error || `Voice save failed (${res.status})`);
      }
      // Refresh voice list and auto-select
      const listRes = await fetchWithTimeout(`${base}/api/elevenlabs/voices`, { timeout: FETCH_LIST_MS });
      const listData = await listRes.json().catch(() => ({ voices: [] }));
      if (Array.isArray(listData.voices)) {
        setElevenVoices(listData.voices);
        if (data.voice_id) setSelectedVoiceId(data.voice_id);
      }
      sfx.tap();
      setCreateVoiceOpen(false);
      setCreateVoiceName('');
      resetCreateVoiceFlow();
    } catch (err) {
      console.error('Save preview error:', err);
      setCreateVoiceError(err.message || 'Could not save voice');
    } finally {
      setCreateVoicePending(false);
    }
  };

  /**
   * Clone flow — start mic recording. Captures audio via MediaRecorder
   * and stores it as base64 once the user stops.
   */
  const handleStartCloneRecording = async () => {
    setCreateVoiceError('');
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mime = MediaRecorder.isTypeSupported('audio/webm;codecs=opus') ? 'audio/webm;codecs=opus' : 'audio/webm';
      const recorder = new MediaRecorder(stream, { mimeType: mime });
      recordedChunksRef.current = [];
      recorder.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) recordedChunksRef.current.push(e.data);
      };
      recorder.onstop = () => {
        const blob = new Blob(recordedChunksRef.current, { type: mime });
        const url = URL.createObjectURL(blob);
        setCloneAudioUrl(url);
        setCloneAudioMime(mime);
        const reader = new FileReader();
        reader.onloadend = () => {
          const result = String(reader.result || '');
          // strip the data URL prefix; backend handles either format
          setCloneAudioBase64(result.replace(/^data:[^;]+;base64,/, ''));
        };
        reader.readAsDataURL(blob);
        stream.getTracks().forEach((t) => t.stop());
      };
      mediaRecorderRef.current = recorder;
      recorder.start();
      setCloneRecording(true);
      sfx.tap();
    } catch (err) {
      console.error('Mic access error:', err);
      setCreateVoiceError('Microphone access denied — allow mic permissions or upload an audio file instead.');
    }
  };

  const handleStopCloneRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }
    setCloneRecording(false);
    sfx.tap();
  };

  /** Clone flow — upload an audio file as an alternative to recording. */
  const handleCloneAudioFile = (file) => {
    if (!file) return;
    setCloneAudioMime(file.type || 'audio/mpeg');
    setCloneAudioUrl(URL.createObjectURL(file));
    const reader = new FileReader();
    reader.onloadend = () => {
      const result = String(reader.result || '');
      setCloneAudioBase64(result.replace(/^data:[^;]+;base64,/, ''));
    };
    reader.readAsDataURL(file);
  };

  /** Clone flow — submit the captured audio to ElevenLabs for cloning. */
  const handleSubmitClone = async () => {
    const name = createVoiceName.trim();
    if (!name) { setCreateVoiceError('Give your voice a name.'); return; }
    if (!cloneAudioBase64) { setCreateVoiceError('Record or upload a voice sample first.'); return; }
    setCreateVoicePending(true);
    setCreateVoiceError('');
    try {
      const base = API_BASE.replace(/\/$/, '');
      const res = await fetchWithTimeout(`${base}/api/elevenlabs/clone-voice`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, audio_base64: cloneAudioBase64, mime_type: cloneAudioMime }),
        timeout: 90000,
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.ok) {
        throw new Error(data.message || data.error || `Clone failed (${res.status})`);
      }
      // Refresh + auto-select
      const listRes = await fetchWithTimeout(`${base}/api/elevenlabs/voices`, { timeout: FETCH_LIST_MS });
      const listData = await listRes.json().catch(() => ({ voices: [] }));
      if (Array.isArray(listData.voices)) {
        setElevenVoices(listData.voices);
        if (data.voice_id) setSelectedVoiceId(data.voice_id);
      }
      sfx.tap();
      setCreateVoiceOpen(false);
      setCreateVoiceName('');
      resetCreateVoiceFlow();
    } catch (err) {
      console.error('Clone error:', err);
      setCreateVoiceError(err.message || 'Could not clone voice');
    } finally {
      setCreateVoicePending(false);
    }
  };

  /**
   * Edit-from-result handler — pulls the original prompt off a result item,
   * drops it into the Description textarea, then scrolls + focuses the
   * textarea so the user can tweak and re-generate. Tries every reasonable
   * shape we've seen for stored items (top-level, .data, .meta).
   */
  const handleEditFromResult = (item) => {
    const originalPrompt =
      item?.prompt ||
      item?.data?.prompt ||
      item?.data?.description ||
      item?.meta?.prompt ||
      '';
    if (originalPrompt) setPrompt(originalPrompt);
    sfx.tap();
    // Close the expand modal if it's open
    setViewImageUrl(null);
    // Defer to next tick so React paints the new prompt before we scroll
    setTimeout(() => {
      if (promptRef.current) {
        promptRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
        promptRef.current.focus({ preventScroll: true });
      }
    }, 30);
  };

  const buildReferenceImagesBySection = () => {
    const bySection = {};
    const faceRef = characterRefs.find((r) => r?.dataBase64) || refCharacter;
    if (faceRef?.dataBase64) bySection.face = [faceRef];
    if (refBackground?.dataBase64) bySection.background = [refBackground];
    if (refDialog?.dataBase64) bySection.permanent = [refDialog];
    return Object.keys(bySection).length ? bySection : null;
  };

  const handleCheckImages = async () => {
    const payload = {};
    const charRefs = characterRefs.filter((r) => r && r.dataBase64);
    if (charRefs.length > 0) payload.characterReferences = charRefs.map((r) => ({ mimeType: r.mimeType || 'image/jpeg', dataBase64: r.dataBase64 }));
    if (startFrame?.dataBase64) payload.startFrame = { mimeType: startFrame.mimeType || 'image/jpeg', dataBase64: startFrame.dataBase64 };
    if (endFrame?.dataBase64) payload.endFrame = { mimeType: endFrame.mimeType || 'image/jpeg', dataBase64: endFrame.dataBase64 };
    if (Object.keys(payload).length === 0) {
      setError('Add at least one reference image (character, start frame, or end frame) to check.');
      return;
    }
    setError('');
    setCheckResult(null);
    setCheckingImages(true);
    try {
      const res = await fetchWithTimeout(`${API_BASE}/api/veo/check-reference-images`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        timeout: 30000,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || data.error || 'Check failed');
      setCheckResult({ ok: data.ok, message: data.message, score: data.score, tips: data.tips || [] });
    } catch (e) {
      setCheckResult({ ok: false, message: e.message || 'Check failed', tips: [] });
    } finally {
      setCheckingImages(false);
    }
  };

  const downloadImage = async (src, idx = 0) => {
    // Telemetry: tell the backend the user downloaded an image. Lets
    // userDetective learn which image styles / tiers the user actually
    // saves vs discards — a strong signal of taste.
    try {
      if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function' && API_BASE) {
        const filename = (src || '').split('/').pop()?.split('?')[0] || '';
        const body = JSON.stringify({
          event: 'image_download',
          detail: { filename, slot: idx },
          ...ariaUserIdJsonBody(),
        });
        navigator.sendBeacon(`${API_BASE}/api/assist/event`, new Blob([body], { type: 'application/json' }));
      }
    } catch (_) { /* non-fatal */ }
    // Prefer the backend's force-download endpoint when the image lives on
    // our server — it sets Content-Disposition: attachment so the browser
    // saves instead of navigating.
    try {
      const filename = (src || '').split('/').pop()?.split('?')[0] || '';
      const looksLikeBackendImage = filename && /\.(png|jpe?g|webp)$/i.test(filename);
      if (looksLikeBackendImage) {
        const dlUrl = `${API_BASE}/api/images/download?file=${encodeURIComponent(filename)}`;
        const a = document.createElement('a');
        a.href = dlUrl;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        a.remove();
        return;
      }
    } catch (_) { /* fall through */ }
    // Fallback: blob fetch (same-origin or CORS-allowed) → object URL → click
    try {
      const res = await fetch(src, { mode: 'cors' });
      const blob = await res.blob();
      const ext = (blob.type?.split('/')?.[1] || 'png').replace('jpeg', 'jpg');
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `aria-image-${Date.now()}-${idx + 1}.${ext}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) {
      // Last resort: open in a new tab so the user can right-click → Save Image As
      window.open(src, '_blank', 'noopener,noreferrer');
    }
  };

  const handleGenerate = async () => {
    if (!prompt.trim() && !dialogText.trim()) {
      setError('Enter a scene or dialog.');
      return;
    }
    // Audio generation pipeline isn't wired yet — show a friendly notice so
    // pressing Generate in Audio mode doesn't accidentally call the Veo route.
    if (mode === MODE_AUDIO) {
      setError('Audio generation is coming soon. The audio model and pricing are visible — wiring the backend route is next.');
      sfx.error();
      return;
    }
    if (mode === MODE_VIDEO && videoImageMode === VIDEO_IMAGE_MODE_START_END) {
      if (!startFrame?.dataBase64 || !endFrame?.dataBase64) {
        setError('Add both Start frame and End frame when using Frame to frame mode.');
        return;
      }
    }
    setError('');
    setResult(null);
    setLoading(true);
    setGeneratingTotal(mode === MODE_VIDEO ? generateCount : 1);
    setGeneratingCurrent(0);
    // Estimated time per item (seconds):
    //   - Picture (nano banana, single): ~20s
    //   - Picture batch: ~18s per image, can run in parallel-ish on server
    //   - Video (Veo Fast, 4-8s): ~75s
    //   - Video (Veo standard, 8s): ~150s
    //   - Video sound stitching adds ~10-15s when ElevenLabs voice / music
    const perItemSeconds = mode === MODE_PICTURE
      ? 20
      : (veoModel && /fast/i.test(String(veoModel)) ? 75 : 150);
    const items = mode === MODE_VIDEO ? generateCount : 1;
    const audioOverhead = (mode === MODE_VIDEO && (voiceMode === 'custom' || addBackgroundMusic)) ? 15 : 0;
    setGenerationStartedAt(Date.now());
    setGenerationEstSeconds(perItemSeconds * items + audioOverhead);
    try {
      if (mode === MODE_PICTURE) {
        setGeneratingCurrent(1);
        const body = {
          prompt: prompt.trim(),
          count: Math.min(4, Math.max(1, generateCount)),
          resolution: imageSize,
          aspectRatio: aspectRatio,
          tier: nanoTier,
        };
        const flatRefs = pictureRefs
          .filter((r) => r && r.dataBase64)
          .map((r) => ({ mimeType: r.mimeType || 'image/jpeg', dataBase64: r.dataBase64 }));
        if (flatRefs.length > 0) {
          body.referenceImages = flatRefs;
          body.referenceImagesAreSamePerson = false;
        }
        const res = await fetchWithTimeout(`${API_BASE}/api/images/generate-from-prompt`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
          timeout: 60000,
        });
        const data = await res.json();
        if (!res.ok) {
          if (data.error === 'FEATURE_LOCKED' || res.status === 403) {
            throw new Error(`__LOCKED__${data.block || 'Image generation is locked on the free plan. Sign in or upgrade to unlock.'}`);
          }
          throw new Error(data.message || data.error || res.statusText);
        }
        const next = { type: 'image', images: data.images || [], model: nanoTier };
        setResult(next);
        setResultsList((prev) => [next, ...prev]);
        sfx.complete();
        // Tell the monthly-spend badge to refresh — image_log just ticked up.
        try { window.dispatchEvent(new CustomEvent('imagegen:results-changed')); } catch (_) {}
      } else {
        const count = Math.min(4, Math.max(1, generateCount));
        for (let i = 0; i < count; i++) {
          setGeneratingCurrent(i + 1);
          setGeneratingTotal(count);
          // Combine Scene + Dialog into one prompt for Veo (UI keeps them separate for clarity)
          const sceneText = prompt.trim();
          const dialogOnly = dialogText.trim();
          const combinedPromptForVeo = sceneText + (dialogOnly ? ` Dialog: "${dialogOnly}"` : '');
          const body = {
          prompt: combinedPromptForVeo,
          model: veoModel,
          durationSeconds: veoDuration,
          resolution: veoResolution,
          characterText: '',
          backgroundText: '',
          dialogText: dialogOnly,
          // In "Auto (Veo sound)" we want Veo to provide the audio; ElevenLabs is only used in "Custom".
          addBackgroundMusic: voiceMode === 'custom' ? addBackgroundMusic : false,
          // Send through the mode so backend can hard-enforce "Auto" semantics.
          voiceMode,
        };
        if (videoImageMode === VIDEO_IMAGE_MODE_START_END && startFrame?.dataBase64 && endFrame?.dataBase64) {
          body.startFrame = startFrame.dataBase64;
          body.startFrameMimeType = startFrame.mimeType || 'image/jpeg';
          body.endFrame = endFrame.dataBase64;
          body.endFrameMimeType = endFrame.mimeType || 'image/jpeg';
        }
        const charRefs = characterRefs.filter((r) => r && r.dataBase64);
        if (videoImageMode === VIDEO_IMAGE_MODE_REF && charRefs.length > 0) {
          body.characterReferences = charRefs.map((r) => ({ mimeType: r.mimeType || 'image/jpeg', dataBase64: r.dataBase64 }));
        }
        // Only send voiceId to ElevenLabs when "Custom" sound is selected.
        if (voiceMode === 'custom') {
          const voiceToSend = selectedVoiceId || (elevenVoices.length > 0 ? elevenVoices[0].voice_id : null);
          if (voiceToSend) body.voiceId = voiceToSend;
        }
        const res = await fetchWithTimeout(`${API_BASE}/api/veo`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
          timeout: 300000,
        });
        const data = await res.json();
        if (!res.ok) {
          if (data.error === 'FEATURE_LOCKED' || res.status === 403) {
            throw new Error(`__LOCKED__${data.block || 'Video generation is locked on the free plan. Sign in or upgrade to unlock.'}`);
          }
          const msg = data.error === "MISSING_PROMPT" || (data.message && /prompt|character|background|dialog/i.test(data.message))
            ? "Enter a prompt."
            : (data.message || data.error || res.statusText);
          throw new Error(msg);
        }
        const next = { type: 'video', data, model: veoModel, durationSeconds: veoDuration };
        setResult(next);
        setResultsList((prev) => [next, ...prev]);
        sfx.complete();
        // Refresh library so the new video appears in saved-videos / history everywhere (e.g. Aria / Update)
        const base = API_BASE || '';
        fetchWithTimeout(`${base}/api/veo/saved-videos?${USER_CREATED_QUERY}&${FINAL_ONLY_QUERY}`, { timeout: 10000 })
          .then((r) => r.json())
          .then((saved) => {
            if (saved?.ok && Array.isArray(saved.videos) && saved.videos.length > 0) {
              setSavedVideos(saved.videos);
            }
          })
          .catch(() => {});
        fetchWithTimeout(`${base}/api/image-gen/history?limit=${IMAGEN_GEN_RESULTS_MAX}`, { timeout: 10000 })
          .then((r) => r.json())
          .then((hist) => {
            if (hist?.ok && Array.isArray(hist.items) && hist.items.length > 0) {
              startTransition(() => setResultsList(hist.items));
            }
          })
          .catch(() => {});
        }
      }
    } catch (e) {
      setError(e.message || 'Request failed');
      sfx.error();
    } finally {
      setLoading(false);
      setGeneratingCurrent(0);
      setGeneratingTotal(0);
    }
  };

  return (
    <div className={`image-gen-view image-gen-view--${mode}`}>
      {/* Title sits on its OWN row, separate from the toggle row below it. */}
      <h2 className="image-gen-title">AI Generator</h2>
      <header className="image-gen-header">
        {/* Far-left: Image / Video toggle on a separate row from the title. */}
        <div className="image-gen-mode-top">
          <button
            type="button"
            className={`image-gen-mode-top-btn ${mode === MODE_PICTURE ? 'active' : ''}`}
            onClick={() => { sfx.toggle(); setMode(MODE_PICTURE); }}
            disabled={loading}
          >
            Image
          </button>
          <button
            type="button"
            className={`image-gen-mode-top-btn ${mode === MODE_VIDEO ? 'active' : ''}`}
            onClick={() => { sfx.toggle(); setMode(MODE_VIDEO); }}
            disabled={loading}
          >
            Video
          </button>
          <button
            type="button"
            className={`image-gen-mode-top-btn ${mode === MODE_AUDIO ? 'active' : ''}`}
            onClick={() => { sfx.toggle(); setMode(MODE_AUDIO); }}
            disabled={loading}
          >
            Audio
          </button>
        </div>
      </header>

      <div className={`image-gen-layout ${resultsPanelVisible ? '' : 'image-gen-layout--panel-hidden'}`}>
        {!resultsPanelVisible && (
          <button
            type="button"
            className="image-gen-results-show-btn"
            onClick={() => {
              sfx.click();
              setResultsPanelVisible(true);
              try { localStorage.setItem('aria_imagegen_panel_visible', '1'); } catch (_) {}
            }}
            aria-label="Show results panel"
            title="Show results panel"
          >
            ‹ Results
          </button>
        )}
        <div className="image-gen-form">
        {/* Video: choose ONE image mode — Veo API allows reference images OR start/end frame, not both */}
        {mode === MODE_VIDEO && (
          <div className="image-gen-field image-gen-field-video-mode">
            <label className="image-gen-label">Video model</label>
            {/* Two-option switch — segmented control instead of a dropdown.
                Same active-yellow visual as the Image/Video toggle at the top. */}
            <div className="image-gen-segmented" role="tablist" aria-label="Video model">
              <button
                type="button"
                role="tab"
                aria-selected={videoImageMode === VIDEO_IMAGE_MODE_REF}
                className={`image-gen-segmented-btn ${videoImageMode === VIDEO_IMAGE_MODE_REF ? 'active' : ''}`}
                onClick={() => { sfx.toggle(); setVideoImageMode(VIDEO_IMAGE_MODE_REF); }}
                disabled={loading}
              >
                Reference image
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={videoImageMode === VIDEO_IMAGE_MODE_START_END}
                className={`image-gen-segmented-btn ${videoImageMode === VIDEO_IMAGE_MODE_START_END ? 'active' : ''}`}
                onClick={() => { sfx.toggle(); setVideoImageMode(VIDEO_IMAGE_MODE_START_END); }}
                disabled={loading}
              >
                Frame to frame
              </button>
            </div>
          </div>
        )}

        {mode === MODE_VIDEO && videoImageMode === VIDEO_IMAGE_MODE_REF && (() => {
          // Same single-drop-zone pattern as Picture mode: show added thumbnails
          // to the LEFT of one persistent "+ Drop or click" zone, with a live
          // X/3 counter next to the label. Veo accepts up to 3 references.
          const filledCharRefs = (characterRefs || [])
            .map((ref, idx) => ({ ref, idx }))
            .filter((x) => x.ref);
          const charRefCount = filledCharRefs.length;
          const nextEmptyCharIdx = (characterRefs || []).findIndex((r) => !r);
          const canAddMoreChars = nextEmptyCharIdx >= 0 && charRefCount < CHARACTER_REF_SLOTS;
          return (
        <div className="image-gen-field" data-section="character-ref">
          <label className="image-gen-label">
            Reference image — {charRefCount}/{CHARACTER_REF_SLOTS}
          </label>
          <div className="image-gen-refs image-gen-refs-row" role="list" aria-label="Reference image">
            {filledCharRefs.map(({ ref, idx }) => (
              <div key={`charref-${idx}`} className="image-gen-ref-slot image-gen-ref-slot--filled">
                <div className="image-gen-ref-preview">
                  <img src={`data:${ref.mimeType};base64,${ref.dataBase64}`} alt={`Reference ${idx + 1}`} />
                  <button
                    type="button"
                    className="image-gen-ref-remove"
                    onClick={() => { sfx.click(); removeRef(`characterRef${idx}`); }}
                    aria-label="Remove"
                  >×</button>
                </div>
              </div>
            ))}
            {canAddMoreChars && (
              <div
                className="image-gen-ref-slot image-gen-ref-slot--add image-gen-ref-slot-droppable"
                onDragOver={handleDragOver}
                onDrop={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  const file = e.dataTransfer?.files?.[0];
                  const targetIdx = (characterRefs || []).findIndex((r) => !r);
                  if (file && file.type.startsWith('image/') && targetIdx >= 0) {
                    handleRefFile(`characterRef${targetIdx}`, file);
                  }
                }}
              >
                <button
                  type="button"
                  className="image-gen-ref-add image-gen-ref-droppable"
                  onClick={(e) => { sfx.click(); e.currentTarget.nextElementSibling?.click(); }}
                >
                  + Add or drop
                </button>
                <input
                  type="file"
                  accept="image/*"
                  className="image-gen-ref-input"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    const targetIdx = (characterRefs || []).findIndex((r) => !r);
                    if (file && targetIdx >= 0) handleRefFile(`characterRef${targetIdx}`, file);
                    e.target.value = '';
                  }}
                />
              </div>
            )}
          </div>
          {(characterRefs.some(Boolean) && (
            <div className="image-gen-check-row">
              <button
                type="button"
                className="image-gen-check-btn"
                onClick={() => { sfx.click(); handleCheckImages(); }}
                disabled={checkingImages || loading}
              >
                {checkingImages ? 'Checking…' : 'Check images'}
              </button>
              {checkResult && (
                <div className={`image-gen-check-result image-gen-check-result--${checkResult.ok ? 'ok' : 'warn'}`}>
                  <p className="image-gen-check-message">{checkResult.message}</p>
                  {checkResult.score != null && <p className="image-gen-check-score">Score: {checkResult.score}/10</p>}
                  {checkResult.tips && checkResult.tips.length > 0 && (
                    <ul className="image-gen-check-tips">
                      {checkResult.tips.map((tip, i) => (
                        <li key={i}>{tip}</li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
          );
        })()}

        {mode === MODE_VIDEO && videoImageMode === VIDEO_IMAGE_MODE_START_END && (
          <div className="image-gen-field" data-section="start-end-frames">
            <label className="image-gen-label">Start frame & end frame</label>
            <div className="image-gen-refs image-gen-frames">
              <div className="image-gen-ref-slot">
                <span className="image-gen-ref-name">Start frame</span>
                {startFrame ? (
                  <div className="image-gen-ref-preview">
                    <img src={`data:${startFrame.mimeType};base64,${startFrame.dataBase64}`} alt="Start frame" />
                    <button type="button" className="image-gen-ref-remove" onClick={() => { sfx.click(); removeRef('startFrame'); }} aria-label="Remove">×</button>
                  </div>
                ) : (
                  <button
                    type="button"
                    className="image-gen-ref-add image-gen-ref-droppable"
                    onClick={() => { sfx.click(); startFrameRef.current?.click(); }}
                    onDragOver={handleDragOver}
                    onDrop={(e) => handleDrop('startFrame', e)}
                  >
                    + Add or drop
                  </button>
                )}
                <input
                  ref={startFrameRef}
                  type="file"
                  accept="image/*"
                  className="image-gen-ref-input"
                  onChange={(e) => handleRefFile('startFrame', e.target.files?.[0])}
                />
              </div>
              <div className="image-gen-ref-slot">
                <span className="image-gen-ref-name">End frame</span>
                {endFrame ? (
                  <div className="image-gen-ref-preview">
                    <img src={`data:${endFrame.mimeType};base64,${endFrame.dataBase64}`} alt="End frame" />
                    <button type="button" className="image-gen-ref-remove" onClick={() => { sfx.click(); removeRef('endFrame'); }} aria-label="Remove">×</button>
                  </div>
                ) : (
                  <button
                    type="button"
                    className="image-gen-ref-add image-gen-ref-droppable"
                    onClick={() => { sfx.click(); endFrameRef.current?.click(); }}
                    onDragOver={handleDragOver}
                    onDrop={(e) => handleDrop('endFrame', e)}
                  >
                    + Add or drop
                  </button>
                )}
                <input
                  ref={endFrameRef}
                  type="file"
                  accept="image/*"
                  className="image-gen-ref-input"
                  onChange={(e) => handleRefFile('endFrame', e.target.files?.[0])}
                />
              </div>
            </div>
            {(startFrame || endFrame) && (
              <div className="image-gen-check-row">
                <button
                  type="button"
                  className="image-gen-check-btn"
                  onClick={() => { sfx.click(); handleCheckImages(); }}
                  disabled={checkingImages || loading}
                >
                  {checkingImages ? 'Checking…' : 'Check images'}
                </button>
                {checkResult && (
                  <div className={`image-gen-check-result image-gen-check-result--${checkResult.ok ? 'ok' : 'warn'}`}>
                    <p className="image-gen-check-message">{checkResult.message}</p>
                    {checkResult.score != null && <p className="image-gen-check-score">Score: {checkResult.score}/10</p>}
                    {checkResult.tips && checkResult.tips.length > 0 && (
                      <ul className="image-gen-check-tips">
                        {checkResult.tips.map((tip, i) => (
                          <li key={i}>{tip}</li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        <div className="image-gen-field image-gen-field--description">
          <label className="image-gen-label">Description</label>
          <textarea
            ref={promptRef}
            className="image-gen-prompt"
            placeholder={mode === MODE_VIDEO ? "Describe your video: character, scene, pacing. Put words the character says in quotes, e.g. He says \"Let's go\" and lifts the weight." : "Describe the image you want to generate…"}
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            rows={4}
            disabled={loading}
          />
        </div>
        {mode === MODE_PICTURE && (
        <>
          {(() => {
            // ── Single drop-zone reference UI ─────────────────────────
            //   Show ONE "+ Add or drop" box with a live counter X/N.
            //   Each added image appears as a thumbnail to the LEFT of
            //   the drop zone; the counter ticks up. Drop zone hides
            //   when MAX_PICTURE_REFS reached. Replaces the old 10-cell
            //   grid (which always showed 10 empty boxes — too noisy).
            const filledRefs = pictureRefs
              .map((ref, idx) => ({ ref, idx }))
              .filter((x) => x.ref);
            const filledCount = filledRefs.length;
            const nextEmptyIdx = pictureRefs.findIndex((r) => !r);
            const canAddMore = nextEmptyIdx >= 0;
            return (
              <div className="image-gen-field">
                <label className="image-gen-label">
                  Reference images — {filledCount}/{MAX_PICTURE_REFS}
                </label>
                <div className="image-gen-refs image-gen-refs-row">
                  {filledRefs.map(({ ref, idx }) => (
                    <div key={`picref-${idx}`} className="image-gen-ref-slot image-gen-ref-slot--filled">
                      <div className="image-gen-ref-preview">
                        <img src={`data:${ref.mimeType};base64,${ref.dataBase64}`} alt={`Reference ${idx + 1}`} />
                        <button
                          type="button"
                          className="image-gen-ref-remove"
                          onClick={() => { sfx.click(); removeRef(`pictureRef${idx}`); }}
                          aria-label="Remove"
                        >×</button>
                      </div>
                    </div>
                  ))}
                  {canAddMore && (
                    <div className="image-gen-ref-slot image-gen-ref-slot--add image-gen-ref-slot-droppable">
                      <button
                        type="button"
                        className="image-gen-ref-add image-gen-ref-droppable"
                        onClick={(e) => { sfx.click(); e.currentTarget.nextElementSibling?.click(); }}
                        onDragOver={handleDragOver}
                        onDrop={(e) => {
                          const targetIdx = pictureRefs.findIndex((r) => !r);
                          if (targetIdx >= 0) handleDrop(`pictureRef${targetIdx}`, e);
                        }}
                      >
                        + Add or drop
                      </button>
                      <input
                        type="file"
                        accept="image/*"
                        className="image-gen-ref-input"
                        onChange={(e) => {
                          const targetIdx = pictureRefs.findIndex((r) => !r);
                          if (targetIdx >= 0) handleRefFile(`pictureRef${targetIdx}`, e.target.files?.[0]);
                          // Reset so the same file can be re-picked later.
                          e.target.value = '';
                        }}
                      />
                    </div>
                  )}
                </div>
              </div>
            );
          })()}
          <div className="image-gen-field">
            {/* 3-column header above the Model row — labels line up with the
                3-column option layout below (★ quality / brand / price). */}
            <div className="image-gen-label image-gen-label--3col" aria-hidden="true">
              <span className="image-gen-label__left">Quality</span>
              <span className="image-gen-label__center">Model</span>
              <span className="image-gen-label__right">Price</span>
            </div>
            <GlassSelect
              className="image-gen-select"
              value={nanoTier}
              onChange={setNanoTier}
              disabled={loading}
              ariaLabel="Model"
              options={[
                {
                  value: NANO_TIER_PRO,
                  label: 'Nano Banana Pro',
                  meta: {
                    quality: MODEL_INFO[NANO_TIER_PRO].quality,
                    price: priceLabel(MODEL_INFO[NANO_TIER_PRO].rate, MODEL_INFO[NANO_TIER_PRO].unit),
                    emoji: '🍌',
                  },
                },
                {
                  value: NANO_TIER_FAST,
                  label: 'Nano Banana Fast',
                  meta: {
                    quality: MODEL_INFO[NANO_TIER_FAST].quality,
                    price: priceLabel(MODEL_INFO[NANO_TIER_FAST].rate, MODEL_INFO[NANO_TIER_FAST].unit),
                    emoji: '🍌',
                  },
                },
              ]}
            />
          </div>
          {/* ── Pixel + Aspect-ratio row ─ Pixel on the left, visual frame
              shapes on the right. Each frame is a mini-rectangle drawn in
              its actual aspect ratio so 16:9 looks landscape, 9:16 looks
              portrait, etc. Number label sits above each frame. */}
          <div className="image-gen-pixel-row">
            <div className="image-gen-field image-gen-pixel-field">
              <label className="image-gen-label">Pixel</label>
              <GlassSelect
                className="image-gen-select image-gen-select--compact"
                value={imageSize}
                onChange={setImageSize}
                disabled={loading}
                ariaLabel="Pixel"
                options={[SIZE_2K, SIZE_4K].map((sz) => ({
                  value: sz,
                  label: SIZE_INFO[sz].label,
                }))}
              />
            </div>
            {/* ── Aspect ratio dropdown ─ closed trigger shows only the
                ratio number (e.g. "9:16"). The open menu shows each
                option as [shape rectangle] + [ratio number] so users
                can see what shape they're picking. The label JSX wraps
                both pieces; CSS hides the shape when shown inside the
                closed trigger. */}
            <div className="image-gen-field image-gen-aspect-field">
              <label className="image-gen-label">Aspect</label>
              <GlassSelect
                className="image-gen-select image-gen-select--compact image-gen-aspect-select"
                value={aspectRatio}
                onChange={(v) => { sfx.tap(); setAspectRatio(v); }}
                disabled={loading}
                ariaLabel="Aspect ratio"
                options={[
                  { value: '1:1',  w: 1,  h: 1  },
                  { value: '4:3',  w: 4,  h: 3  },
                  { value: '3:4',  w: 3,  h: 4  },
                  { value: '16:9', w: 16, h: 9  },
                  { value: '9:16', w: 9,  h: 16 },
                ].map((ar) => {
                  // Scale the frame to fit in a 22×22 box, longest side = 22px.
                  const MAX = 22;
                  const scale = MAX / Math.max(ar.w, ar.h);
                  const w = ar.w * scale;
                  const h = ar.h * scale;
                  return {
                    value: ar.value,
                    label: (
                      <span className="image-gen-aspect-row">
                        <span
                          className="image-gen-aspect-row__frame"
                          style={{ width: `${w}px`, height: `${h}px` }}
                          aria-hidden="true"
                        />
                        <span className="image-gen-aspect-row__num">{ar.value}</span>
                      </span>
                    ),
                  };
                })}
              />
            </div>
          </div>
        </>
        )}

        {mode === MODE_AUDIO && (
          <>
            <div className="image-gen-field">
              <label className="image-gen-label">Audio model</label>
              <GlassSelect
                className="image-gen-select"
                value={audioModel}
                onChange={setAudioModel}
                disabled={loading}
                ariaLabel="Audio model"
                options={[
                  {
                    value: AUDIO_MODEL_STUDIO,
                    label: 'ElevenLabs Studio',
                    meta: {
                      quality: MODEL_INFO[AUDIO_MODEL_STUDIO].quality,
                      price: priceLabel(MODEL_INFO[AUDIO_MODEL_STUDIO].rate, MODEL_INFO[AUDIO_MODEL_STUDIO].unit),
                      emoji: '🎵',
                    },
                  },
                  {
                    value: AUDIO_MODEL_FAST,
                    label: 'ElevenLabs Fast',
                    meta: {
                      quality: MODEL_INFO[AUDIO_MODEL_FAST].quality,
                      price: priceLabel(MODEL_INFO[AUDIO_MODEL_FAST].rate, MODEL_INFO[AUDIO_MODEL_FAST].unit),
                      emoji: '🎵',
                    },
                  },
                ]}
              />
            </div>
            <div className="image-gen-field">
              <label className="image-gen-label">Duration</label>
              <GlassSelect
                className="image-gen-select"
                value={audioDuration}
                onChange={(v) => setAudioDuration(Number(v))}
                disabled={loading}
                ariaLabel="Duration"
                options={AUDIO_DURATIONS.map((sec) => ({ value: sec, label: `${sec} sec` }))}
              />
            </div>
          </>
        )}

        {/* ── Sound (left) + Voice (right) side-by-side row ───────────
            Sound picks the audio source (Veo's own track or ElevenLabs).
            Voice column lists ONLY voices the user created themselves
            (instant-clone / professional-clone / voice-designer), plus
            a "+ Create a voice" button below to make a new one via the
            ElevenLabs Voice Designer.
            Visibility rules (updated 2026-06-02 per owner):
              • Sound col → only in video mode
              • Voice col → ONLY in video mode when Custom (ElevenLabs)
                is picked. Image mode no longer shows a voice picker. */}
        <div className="image-gen-sound-voice-row">
            {mode === MODE_VIDEO && (
              <div className="image-gen-sound-voice-col image-gen-sound-col">
                <label className="image-gen-voices-label">Sound</label>
                <GlassSelect
                  className="image-gen-voice-select image-gen-sound-select"
                  value={voiceMode}
                  onChange={setVoiceMode}
                  disabled={loading}
                  ariaLabel="Sound"
                  options={[
                    { value: 'auto', label: 'Auto (Veo sound)' },
                    { value: 'custom', label: 'Custom (ElevenLabs)' },
                  ]}
                />
              </div>
            )}

            {mode === MODE_VIDEO && voiceMode === 'custom' && (
              <div className="image-gen-sound-voice-col image-gen-voice-col">
                <label className="image-gen-voices-label">Your voices</label>
                <div className="image-gen-voices-controls">
                  {(() => {
                    // User-made voices only: 'cloned' (instant clone), 'generated'
                    // (Voice Designer), 'professional' (paid clone). Everything else
                    // ('premade' library voices) is excluded here.
                    const userVoices = elevenVoices.filter((v) =>
                      ['cloned', 'generated', 'professional'].includes(String(v.category || '').toLowerCase())
                    );
                    const opts = userVoices.length > 0
                      ? userVoices.map((v) => ({
                          value: v.voice_id,
                          label: `${v.name}${v.labels?.gender ? ` (${v.labels.gender})` : ''}`,
                        }))
                      : [{ value: '', label: elevenLabsConfigured === false
                          ? 'No voices — add API key'
                          : 'No voices yet — create one' }];
                    return (
                      <GlassSelect
                        className="image-gen-voice-select"
                        value={selectedVoiceId}
                        onChange={setSelectedVoiceId}
                        disabled={loading || userVoices.length === 0}
                        ariaLabel="Voice"
                        options={opts}
                      />
                    );
                  })()}
                  <button
                    type="button"
                    className="image-gen-voice-play"
                    onClick={() => { sfx.click(); playVoicePreview(); }}
                    disabled={loading || !selectedVoiceId || voicePreviewPlaying}
                    title="Play voice sample"
                    aria-label="Play voice sample"
                  >
                    {voicePreviewPlaying ? (
                      <span className="image-gen-voice-play-loading" aria-hidden="true">...</span>
                    ) : (
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                        <path d="M8 5v14l11-7z" />
                        <path d="M6 19V5H4v14h2z" />
                      </svg>
                    )}
                  </button>
                </div>
                {/* "Create a voice" button — sits BELOW the dropdown so the
                    user sees it before opening the menu. */}
                <button
                  type="button"
                  className="image-gen-create-voice-btn"
                  onClick={() => { sfx.click(); setCreateVoiceError(''); setCreateVoiceOpen(true); }}
                  disabled={loading || elevenLabsConfigured === false}
                  title={elevenLabsConfigured === false
                    ? 'Add ELEVENLABS_API_KEY to enable voice creation'
                    : 'Create a brand new voice using ElevenLabs Voice Designer'}
                >
                  <span className="image-gen-create-voice-btn__plus" aria-hidden="true">+</span>
                  Create a voice
                </button>
                {mode === MODE_VIDEO && voiceMode === 'custom' && elevenLabsConfigured === false && (
                  <p className="image-gen-elevenlabs-hint">ElevenLabs is not configured. Add <code>ELEVENLABS_API_KEY</code> to <code>HowTo-Backend/.env</code> (get key at elevenlabs.io), then restart the backend. Voice and sound will then be added to videos automatically.</p>
                )}
                {mode === MODE_VIDEO && voiceMode === 'custom' && elevenLabsConfigured === true && (
                  <p className="image-gen-elevenlabs-hint image-gen-elevenlabs-hint-ok">Voice and sound are added automatically to videos that don’t have it.</p>
                )}
              </div>
            )}
        </div>

        {mode === MODE_VIDEO && voiceMode === 'custom' && (
          <div className="image-gen-field">
            <label className="image-gen-label" style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={addBackgroundMusic}
                onChange={(e) => setAddBackgroundMusic(e.target.checked)}
                disabled={loading}
              />
              Add background music that fits the vibe
            </label>
          </div>
        )}

        <div className="image-gen-actions-row">
          <div className="image-gen-action-group">
            <label className="image-gen-action-label">Quantity</label>
            <GlassSelect
              className="image-gen-action-select"
              value={generateCount}
              onChange={(v) => setGenerateCount(Number(v))}
              disabled={loading}
              ariaLabel="Quantity"
              options={[1, 2, 3, 4].map((n) => ({ value: n, label: String(n) }))}
            />
          </div>
          {mode === MODE_VIDEO && (
            <>
              <div className="image-gen-action-group">
                <label className="image-gen-action-label">Model</label>
                <GlassSelect
                  className="image-gen-action-select"
                  value={veoModel}
                  onChange={setVeoModel}
                  disabled={loading}
                  ariaLabel="Model"
                  options={[
                    {
                      value: VEO_MODEL_FAST,
                      label: 'Veo 3.1 Fast',
                      meta: {
                        quality: MODEL_INFO[VEO_MODEL_FAST].quality,
                        price: priceLabel(MODEL_INFO[VEO_MODEL_FAST].rate, MODEL_INFO[VEO_MODEL_FAST].unit),
                        emoji: '🎬',
                      },
                    },
                    {
                      value: VEO_MODEL_PRO,
                      label: 'Veo 3.1 Pro',
                      meta: {
                        quality: MODEL_INFO[VEO_MODEL_PRO].quality,
                        price: priceLabel(MODEL_INFO[VEO_MODEL_PRO].rate, MODEL_INFO[VEO_MODEL_PRO].unit),
                        emoji: '🎬',
                      },
                    },
                  ]}
                />
              </div>
              <div className="image-gen-action-group">
                <label className="image-gen-action-label">Duration</label>
                <GlassSelect
                  className="image-gen-action-select"
                  value={veoDuration}
                  onChange={(v) => setVeoDuration(Number(v))}
                  disabled={loading}
                  ariaLabel="Duration"
                  options={VEO_DURATIONS.map((sec) => ({ value: sec, label: `${sec} sec` }))}
                />
              </div>
              <div className="image-gen-action-group">
                <label className="image-gen-action-label">Resolution</label>
                <GlassSelect
                  className="image-gen-action-select"
                  value={veoResolution}
                  onChange={setVeoResolution}
                  disabled={loading}
                  ariaLabel="Resolution"
                  options={VEO_RESOLUTIONS.map((res) => ({ value: res, label: res }))}
                />
              </div>
            </>
          )}
          <button
            type="button"
            className="image-gen-submit"
            onClick={() => { sfx.generate(); handleGenerate(); }}
            disabled={loading || (!prompt.trim() && !dialogText.trim())}
          >
            {loading ? (mode === MODE_PICTURE ? 'Generating image…' : 'Generating video…') : 'Generate'}
          </button>
        </div>

        {error && error.startsWith('__LOCKED__') ? (
          <div className="image-gen-locked" role="alert">
            <div className="image-gen-locked__icon">🔒</div>
            <div className="image-gen-locked__body">
              <strong>Generation is locked</strong>
              <p>{error.replace('__LOCKED__', '')}</p>
              <div className="image-gen-locked__actions">
                <button
                  type="button"
                  className="image-gen-locked__btn"
                  onClick={() => {
                    sfx.click();
                    try { window.dispatchEvent(new CustomEvent('aria-open-signin')); } catch (_) {}
                    const pill = document.querySelector('.account-pill--signin');
                    if (pill && typeof pill.click === 'function') pill.click();
                  }}
                >
                  Sign in to unlock
                </button>
                <button
                  type="button"
                  className="image-gen-locked__dismiss"
                  onClick={() => { sfx.click(); setError(''); }}
                >
                  Dismiss
                </button>
              </div>
            </div>
          </div>
        ) : error ? (
          <p className="image-gen-error">{error}</p>
        ) : null}

        </div>

        <aside className={`image-gen-results-panel ${resultsPanelVisible ? '' : 'image-gen-results-panel--hidden'}`} aria-label="Generated videos and images">
          <div className="image-gen-results-panel-header">
            <div className="image-gen-results-panel-actions">
              <span className="image-gen-results-size-label">Size:</span>
              {/* − / + stepper pill — bumps results-per-row between 1 and 4
                  with a vertical divider between the two buttons. Same
                  dark grey glass body as the rest of the pill family. */}
              <div className="image-gen-results-stepper" role="group" aria-label="Tiles per row">
                <button
                  type="button"
                  className="image-gen-results-stepper-btn"
                  onClick={() => { sfx.tap(); setResultsPerRow((v) => Math.max(1, v - 1)); }}
                  disabled={resultsPerRow <= 1}
                  aria-label="Fewer per row"
                  title="Fewer per row"
                >−</button>
                <span className="image-gen-results-stepper-divider" aria-hidden="true" />
                <button
                  type="button"
                  className="image-gen-results-stepper-btn"
                  onClick={() => { sfx.tap(); setResultsPerRow((v) => Math.min(4, v + 1)); }}
                  disabled={resultsPerRow >= 4}
                  aria-label="More per row"
                  title="More per row"
                >+</button>
              </div>
              {/* Update button removed per design — videos auto-refresh
                  after generation via the imagegen:results-changed event. */}
              {/* Monthly image spend — pushed to the far right of the Size
                  row with margin-left: auto. Ambient cost transparency. */}
              <button
                type="button"
                className={`image-gen-spend-badge ${monthlyBadgeOpen ? 'is-open' : ''}`}
                onClick={() => { sfx.click(); setMonthlyBadgeOpen((v) => !v); }}
                aria-label="Monthly image spend"
                title="Click for breakdown"
              >
                <span className="image-gen-spend-badge__label">This month</span>
                <span className="image-gen-spend-badge__total">
                  ${monthlyImageSpend.total.toFixed(2)}
                </span>
                <span className="image-gen-spend-badge__count">
                  · {monthlyImageSpend.count} image{monthlyImageSpend.count === 1 ? '' : 's'}
                </span>
                {monthlyBadgeOpen && (
                  <div className="image-gen-spend-badge__panel" onClick={(e) => e.stopPropagation()}>
                    <div className="image-gen-spend-badge__panel-head">By model</div>
                    {Object.keys(monthlyImageSpend.byModel || {}).length === 0 ? (
                      <div className="image-gen-spend-badge__panel-empty">No generations yet this month.</div>
                    ) : (
                      Object.entries(monthlyImageSpend.byModel).map(([m, v]) => (
                        <div key={m} className="image-gen-spend-badge__panel-row">
                          <span className="image-gen-spend-badge__panel-model">
                            {m.replace('models/', '').replace('-preview', '')}
                          </span>
                          <span className="image-gen-spend-badge__panel-value">
                            ${(v || 0).toFixed(2)}
                          </span>
                        </div>
                      ))
                    )}
                  </div>
                )}
              </button>
              {resultsList.length > 0 && (
                <button
                  type="button"
                  className="image-gen-results-clear"
                  onClick={() => {
                    if (loading) return;
                    sfx.click();
                    setResultsList([]);
                    setResult(null);
                    try { localStorage.removeItem(IMAGEN_GEN_RESULTS_KEY); } catch (_) {}
                  }}
                  disabled={loading}
                  aria-label="Clear list (keeps videos on disk)"
                >
                  Clear list
                </button>
              )}
            </div>
          </div>
          <div className="image-gen-results-panel-scroll">
          {panelInitialLoading && (
            <p className="image-gen-results-loading" role="status" aria-live="polite">Loading your videos and images…</p>
          )}
          {!panelInitialLoading && mode === MODE_VIDEO && allGeneratedItems.length === 0 && (
            <div className="image-gen-results-empty-glass">
              {/* Placeholder glass tiles so users can preview the Liquid
                  Glass frame before anything's generated. Count matches
                  the Quantity selector. */}
              {/* iPhone Photos-style grid: every placeholder is a uniform
                  1:1 square. Show a "full list" of 4-6 tiles so the gallery
                  layout is visible before anything is generated — number
                  scales with the SIZE 1/2/3/4 selector. */}
              <div
                className={`image-gen-results-empty-grid image-gen-results-grid--${resultsPerRow}`}
                aria-hidden="true"
              >
                {Array.from({
                  // 4 tiles for SIZE=1 (a tall column), 6 tiles for SIZE 2/3
                  // (two clean rows), 8 for SIZE 4 (two rows of 4).
                  length: resultsPerRow === 1 ? 4
                        : resultsPerRow === 4 ? 8
                        : 6,
                }).map((_, i) => (
                  <div
                    key={i}
                    className="image-gen-result-video-block image-gen-result-placeholder"
                  >
                    <div className="image-gen-result-placeholder__inner" />
                  </div>
                ))}
              </div>
            </div>
          )}
          {!panelInitialLoading && allGeneratedItems.length > 0 && (
            <>
            <ul ref={resultsListRef} className={`image-gen-results-list image-gen-results-list-first image-gen-results-grid--${resultsPerRow}`}>
              {visibleItems.map((item, idx) => {
                const imageList = item.type === 'image' ? (item.images?.length > 0 ? item.images : (item.data?.imageUrl ? [{ imageUrl: item.data.imageUrl }] : [])) : [];
                // Model badges (quality on the left, $/unit on the right) — drawn from
                // MODEL_INFO. Falls back to null for legacy items without `model`.
                const info = item.model ? MODEL_INFO[item.model] : null;
                return (
                <li key={item.filename || item.data?.videoUrl || idx} className="image-gen-results-item">
                  {imageList.length > 0 && (
                    <div className="image-gen-result-images">
                      {imageList.map((img, i) => {
                        const src = img.imageUrl?.startsWith('/') ? (API_BASE ? API_BASE + img.imageUrl : img.imageUrl) : img.imageUrl;
                        if (!src) return null;
                        return (
                          <div key={i} className="image-gen-result-img-wrap">
                            {/* Click the image to expand it into the centered
                                bubble modal (rest of gallery blurs behind). */}
                            <img
                              src={src}
                              alt="Generated"
                              className="image-gen-result-img"
                              onClick={() => { sfx.click(); setViewImageUrl(src); }}
                              style={{ cursor: 'zoom-in' }}
                            />
                            {/* TOP-LEFT — edit button. Drops the original
                                prompt into the Description box and scrolls
                                + focuses it for a quick re-prompt. */}
                            <button
                              type="button"
                              className="image-gen-result-edit"
                              title="Edit prompt — drop it into the Description box"
                              aria-label="Edit prompt"
                              onClick={(e) => { e.stopPropagation(); handleEditFromResult(item); }}
                            >
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                                <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04a1 1 0 000-1.41l-2.34-2.34a1 1 0 00-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z" />
                              </svg>
                            </button>
                            <button
                              type="button"
                              className="image-gen-result-download"
                              title="Download image"
                              aria-label="Download image"
                              onClick={(e) => { e.stopPropagation(); sfx.click(); downloadImage(src, i); }}
                            >
                              ⬇
                            </button>
                            {info && (
                              <>
                                <div className="image-gen-result-badge image-gen-result-badge--quality" title={`${info.label} • quality score`}>
                                  <span className="image-gen-result-badge__star" aria-hidden="true">★</span>
                                  <span>{info.quality}</span>
                                </div>
                                {/* Top-right brand badge — model emoji + name */}
                                <div className="image-gen-result-badge image-gen-result-badge--brand" title={info.label}>
                                  <span className="image-gen-result-badge__emoji" aria-hidden="true">{info.emoji}</span>
                                  <span>{info.label}</span>
                                </div>
                                <div className="image-gen-result-badge image-gen-result-badge--cost" title={`${info.label} • billed per ${info.unit}`}>
                                  {priceLabel(info.rate, info.unit)}
                                </div>
                              </>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                  {item.type === 'video' && (() => {
                    const rawUrl = item.data?.videoUrl || (item.filename ? `/videos/${String(item.filename).replace(/^\/videos\//, '')}` : null) || (item.data?.filename ? `/videos/${String(item.data.filename).replace(/^\/videos\//, '')}` : null);
                    const videoUrl = rawUrl && !rawUrl.includes('null') ? (rawUrl.startsWith('http') ? rawUrl : fullVideoUrl(rawUrl)) : null;
                    return (
                      <div className="image-gen-result-video-block">
                        {videoUrl ? (
                          <>
                            <HoverVideo
                              key={videoUrl}
                              className="image-gen-result-video-player"
                              src={videoUrl}
                              controls
                            />
                            {/* TOP-LEFT — edit button. Drops the original
                                video prompt back into the Description box. */}
                            <button
                              type="button"
                              className="image-gen-result-edit"
                              title="Edit prompt — drop it into the Description box"
                              aria-label="Edit prompt"
                              onClick={(e) => { e.stopPropagation(); handleEditFromResult(item); }}
                            >
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                                <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04a1 1 0 000-1.41l-2.34-2.34a1 1 0 00-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z" />
                              </svg>
                            </button>
                            {info && (
                              <>
                                <div className="image-gen-result-badge image-gen-result-badge--quality" title={`${info.label} • quality score`}>
                                  <span className="image-gen-result-badge__star" aria-hidden="true">★</span>
                                  <span>{info.quality}</span>
                                </div>
                                {/* Top-right brand badge — model emoji + name */}
                                <div className="image-gen-result-badge image-gen-result-badge--brand" title={info.label}>
                                  <span className="image-gen-result-badge__emoji" aria-hidden="true">{info.emoji}</span>
                                  <span>{info.label}</span>
                                </div>
                                <div className="image-gen-result-badge image-gen-result-badge--cost" title={`${info.label} • billed per ${info.unit}`}>
                                  {priceLabel(info.rate, info.unit)}
                                </div>
                              </>
                            )}
                          </>
                        ) : (
                          <>
                            {item._storedAudio && item.filename ? (
                              <div className="image-gen-video-placeholder">
                                <p className="image-gen-result-video">Video: {item.filename}</p>
                                <button type="button" className="image-gen-play-stored-sound-btn" onClick={() => { sfx.click(); playWithStoredSound(item.filename); }} disabled={loadingStoredSound === item.filename}>{loadingStoredSound === item.filename ? 'Loading…' : 'Play with stored sound'}</button>
                              </div>
                            ) : (
                              <p className="image-gen-result-video">{item.data?.message || 'Video processing…'}</p>
                            )}
                          </>
                        )}
                      </div>
                    );
                  })()}
                </li>
              );
              })}
            </ul>
            {hasMoreItems && (
              <div className="image-gen-results-show-more-wrap">
                <button type="button" className="image-gen-results-show-more" onClick={() => { sfx.click(); setShowAllResults(true); }}>
                  Show all ({allGeneratedItems.length} items)
                </button>
              </div>
            )}
            </>
          )}
          {mode === MODE_VIDEO && pipelineHealth && (
            <p className="image-gen-pipeline-status" title={pipelineHealth.message || ''}>
              {pipelineHealth.allReady ? (
                <span className="image-gen-pipeline-ok">All systems ready for full video with audio</span>
              ) : (
                <span className="image-gen-pipeline-warn">
                  {[!pipelineHealth.systems?.veo?.ready && 'Veo', !pipelineHealth.systems?.gemini?.ready && 'Gemini', !pipelineHealth.systems?.elevenLabs?.ready && 'ElevenLabs', !pipelineHealth.systems?.ffmpeg?.ready && 'ffmpeg'].filter(Boolean).join(', ')} not configured – video may have no audio
                </span>
              )}
            </p>
          )}
          {loading && (() => {
            const elapsedSec = generationStartedAt ? Math.floor(((nowTick || Date.now()) - generationStartedAt) / 1000) : 0;
            const remainingSec = Math.max(0, generationEstSeconds - elapsedSec);
            const pct = generationEstSeconds > 0 ? Math.min(98, (elapsedSec / generationEstSeconds) * 100) : 0;
            const formatMmSs = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
            const headline = mode === MODE_PICTURE
              ? 'Generating image…'
              : generatingTotal > 1
                ? `Generating video ${Math.max(1, generatingCurrent)} of ${generatingTotal}…`
                : 'Generating video…';
            const isOvertime = elapsedSec > generationEstSeconds;
            return (
              <div className="image-gen-progress" role="status" aria-live="polite">
                <div className="image-gen-progress-header">
                  <p className="image-gen-progress-text">{headline}</p>
                  <span className="image-gen-progress-timer">
                    {isOvertime
                      ? <>Taking longer than expected · <strong>{formatMmSs(elapsedSec)}</strong></>
                      : <>About <strong>{formatMmSs(remainingSec)}</strong> left · {formatMmSs(elapsedSec)} elapsed</>}
                  </span>
                </div>
                <div className="image-gen-progress-bar-wrap">
                  {generatingTotal > 1 ? (
                    <div
                      className="image-gen-progress-bar image-gen-progress-bar-determinate"
                      style={{ width: `${(generatingCurrent / generatingTotal) * 100}%` }}
                    />
                  ) : (
                    <div
                      className={`image-gen-progress-bar image-gen-progress-bar-determinate ${isOvertime ? 'image-gen-progress-bar--overtime' : ''}`}
                      style={{ width: `${pct}%` }}
                    />
                  )}
                </div>
                <p className="image-gen-progress-hint">
                  Estimated total: ~{formatMmSs(generationEstSeconds)}.
                  {' '}{mode === MODE_PICTURE ? 'Images render fast.' : 'Veo videos take time — leave the tab open.'}
                </p>
              </div>
            );
          })()}
          {!panelInitialLoading && !loading && mode !== MODE_VIDEO && allGeneratedItems.length === 0 && (
            <div className="image-gen-results-empty-glass">
              {/* Placeholder glass tiles — same iOS Liquid Glass body as a real
                  result tile, just empty. Count matches what the user will
                  actually generate (Quantity selector). */}
              {/* iPhone Photos-style grid: every placeholder is a uniform
                  1:1 square. Show a "full list" of 4-6 tiles so the gallery
                  layout is visible before anything is generated — number
                  scales with the SIZE 1/2/3/4 selector. */}
              <div
                className={`image-gen-results-empty-grid image-gen-results-grid--${resultsPerRow}`}
                aria-hidden="true"
              >
                {Array.from({
                  // 4 tiles for SIZE=1 (a tall column), 6 tiles for SIZE 2/3
                  // (two clean rows), 8 for SIZE 4 (two rows of 4).
                  length: resultsPerRow === 1 ? 4
                        : resultsPerRow === 4 ? 8
                        : 6,
                }).map((_, i) => (
                  <div
                    key={i}
                    className="image-gen-result-video-block image-gen-result-placeholder"
                  >
                    <div className="image-gen-result-placeholder__inner" />
                  </div>
                ))}
              </div>
            </div>
          )}
          </div>
        </aside>

        {watchVideoUrl && typeof watchVideoUrl === 'string' && watchVideoUrl.trim() !== '' && (
          <div className="image-gen-watch-overlay" role="dialog" aria-modal="true" aria-label="Watch video">
            <div className="image-gen-watch-backdrop" onClick={() => { sfx.click(); setWatchVideoUrl(null); }} aria-hidden />
            <div className="image-gen-watch-modal">
              <div className="image-gen-watch-controls">
                <button
                  type="button"
                  className="image-gen-watch-mute-btn"
                  onClick={() => { sfx.toggle(); setVideosMuted(!videosMuted); }}
                  aria-label={videosMuted ? 'Unmute' : 'Mute'}
                  title={videosMuted ? 'Unmute (applies to all videos)' : 'Mute (applies to all videos)'}
                >
                  {videosMuted ? (
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M16.5 12c0-1.77-1.02-3.29-2.5-4.03v2.21l2.45 2.45c.03-.2.05-.41.05-.63zm2.5 0c0 .94-.2 1.82-.54 2.64l1.51 1.51C20.63 14.91 21 13.5 21 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM4.27 3L3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.06c1.38-.31 2.63-.95 3.69-1.81L19.73 21 21 19.73l-9-9L4.27 3zM12 4L9.91 6.09 12 8.18V4z"/></svg>
                  ) : (
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z"/></svg>
                  )}
                  <span className="image-gen-watch-mute-label">{videosMuted ? 'Unmute' : 'Mute'}</span>
                </button>
                <button type="button" className="image-gen-watch-close" onClick={() => { sfx.click(); setWatchVideoUrl(null); }} aria-label="Close">×</button>
              </div>
              <video
                ref={watchVideoRef}
                className="image-gen-watch-video"
                src={watchVideoUrl}
                controls
                autoPlay
                playsInline
                muted={videosMuted}
              />
            </div>
          </div>
        )}

        {/* ── Image expand modal — click any result image to view it centered
            with the rest of the page blurred behind. Click backdrop or press
            Escape to close. */}
        {viewImageUrl && (
          <div className="image-gen-expand-overlay" role="dialog" aria-modal="true" aria-label="View image">
            <div
              className="image-gen-expand-backdrop"
              onClick={() => { sfx.click(); setViewImageUrl(null); }}
              aria-hidden
            />
            <div className="image-gen-expand-bubble">
              <img
                src={viewImageUrl}
                alt="Expanded"
                className="image-gen-expand-img"
                onClick={() => { sfx.click(); setViewImageUrl(null); }}
              />
              <button
                type="button"
                className="image-gen-expand-close"
                onClick={() => { sfx.click(); setViewImageUrl(null); }}
                aria-label="Close"
              >×</button>
            </div>
          </div>
        )}

        {/* ── Create-a-voice modal — simplified flow:
            • Tab toggle at top: RANDOM (pick gender → generate 3 previews → pick one)
                                  CLONE (record or upload audio → ElevenLabs Instant Clone)
            • Name input (shared across both tabs)
            • Random body: gender select → Generate button → 3 audio previews
            • Clone body: record button (mic) OR file upload → preview + submit */}
        {createVoiceOpen && (
          <div className="image-gen-create-voice-overlay" role="dialog" aria-modal="true" aria-label="Create a voice">
            <div
              className="image-gen-create-voice-backdrop"
              onClick={() => { if (!createVoicePending) { sfx.click(); setCreateVoiceOpen(false); resetCreateVoiceFlow(); } }}
              aria-hidden
            />
            <div className="image-gen-create-voice-modal">
              <button
                type="button"
                className="image-gen-create-voice-close"
                onClick={() => { if (!createVoicePending) { sfx.click(); setCreateVoiceOpen(false); resetCreateVoiceFlow(); } }}
                aria-label="Close"
                disabled={createVoicePending}
              >×</button>

              <h2 className="image-gen-create-voice-title">Create a voice</h2>

              {/* Tab toggle — RANDOM / CLONE */}
              <div className="image-gen-create-voice-tabs" role="tablist">
                <button
                  type="button"
                  role="tab"
                  aria-selected={createVoiceTab === 'random'}
                  className={`image-gen-create-voice-tab ${createVoiceTab === 'random' ? 'is-active' : ''}`}
                  onClick={() => { sfx.click(); setCreateVoiceTab('random'); resetCreateVoiceFlow(); }}
                  disabled={createVoicePending}
                >Random</button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={createVoiceTab === 'clone'}
                  className={`image-gen-create-voice-tab ${createVoiceTab === 'clone' ? 'is-active' : ''}`}
                  onClick={() => { sfx.click(); setCreateVoiceTab('clone'); resetCreateVoiceFlow(); }}
                  disabled={createVoicePending}
                >Clone mine</button>
              </div>

              {/* Voice name — shared across tabs */}
              <label className="image-gen-create-voice-label">Voice name</label>
              <input
                type="text"
                className="image-gen-create-voice-input"
                value={createVoiceName}
                onChange={(e) => setCreateVoiceName(e.target.value)}
                placeholder="e.g. Calm Coach, Energetic Narrator"
                maxLength={60}
                disabled={createVoicePending}
                autoFocus
              />

              {/* ── RANDOM TAB ─────────────────────────────────────────── */}
              {createVoiceTab === 'random' && (
                <>
                  <label className="image-gen-create-voice-label">Gender</label>
                  <select
                    className="image-gen-create-voice-input"
                    value={createVoiceGender}
                    onChange={(e) => { setCreateVoiceGender(e.target.value); resetCreateVoiceFlow(); }}
                    disabled={createVoicePending}
                  >
                    <option value="woman">Woman</option>
                    <option value="man">Man</option>
                  </select>

                  <button
                    type="button"
                    className="image-gen-create-voice-generate"
                    onClick={handleGenerateVoicePreviews}
                    disabled={createVoicePending}
                  >
                    {createVoicePending && createVoicePreviews.length === 0
                      ? 'Generating…'
                      : createVoicePreviews.length > 0 ? 'Generate 3 more' : 'Generate'}
                  </button>

                  {/* The 3 preview cards — only shown after Generate */}
                  {createVoicePreviews.length > 0 && (
                    <div className="image-gen-create-voice-previews">
                      {createVoicePreviews.map((p, i) => {
                        const isSel = createVoiceSelectedIdx === i;
                        return (
                          <div
                            key={p.generated_voice_id}
                            className={`image-gen-create-voice-preview ${isSel ? 'is-selected' : ''}`}
                            onClick={() => { sfx.tap(); setCreateVoiceSelectedIdx(i); }}
                          >
                            <span className="image-gen-create-voice-preview__num">{i + 1}</span>
                            <audio
                              src={p.audio_url}
                              controls
                              className="image-gen-create-voice-preview__audio"
                              onPlay={() => setCreateVoicePlayingIdx(i)}
                              onPause={() => setCreateVoicePlayingIdx((cur) => cur === i ? -1 : cur)}
                              onClick={(e) => e.stopPropagation()}
                            />
                            <span className="image-gen-create-voice-preview__check" aria-hidden="true">
                              {isSel ? '✓' : ''}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </>
              )}

              {/* ── CLONE TAB ──────────────────────────────────────────── */}
              {createVoiceTab === 'clone' && (
                <>
                  <p className="image-gen-create-voice-sub">
                    Record or upload ~30 seconds of your voice. ElevenLabs will clone it instantly.
                  </p>
                  <div className="image-gen-create-voice-clone-actions">
                    {!cloneRecording ? (
                      <button
                        type="button"
                        className="image-gen-create-voice-record"
                        onClick={handleStartCloneRecording}
                        disabled={createVoicePending}
                      >
                        🎙️ Record from microphone
                      </button>
                    ) : (
                      <button
                        type="button"
                        className="image-gen-create-voice-record is-recording"
                        onClick={handleStopCloneRecording}
                      >
                        ⏹ Stop recording
                      </button>
                    )}
                    <label className="image-gen-create-voice-upload">
                      <input
                        type="file"
                        accept="audio/*"
                        onChange={(e) => handleCloneAudioFile(e.target.files?.[0])}
                        disabled={createVoicePending || cloneRecording}
                        hidden
                      />
                      <span>📁 Or upload audio file</span>
                    </label>
                  </div>
                  {cloneAudioUrl && (
                    <div className="image-gen-create-voice-clone-preview">
                      <audio src={cloneAudioUrl} controls />
                    </div>
                  )}
                </>
              )}

              {createVoiceError && (
                <p className="image-gen-create-voice-error">{createVoiceError}</p>
              )}

              <div className="image-gen-create-voice-actions">
                <button
                  type="button"
                  className="image-gen-create-voice-cancel"
                  onClick={() => { sfx.click(); setCreateVoiceOpen(false); resetCreateVoiceFlow(); }}
                  disabled={createVoicePending}
                >Cancel</button>
                <button
                  type="button"
                  className="image-gen-create-voice-submit"
                  onClick={createVoiceTab === 'random' ? handleSaveSelectedPreview : handleSubmitClone}
                  disabled={
                    createVoicePending ||
                    !createVoiceName.trim() ||
                    (createVoiceTab === 'random'
                      ? createVoiceSelectedIdx < 0
                      : !cloneAudioBase64)
                  }
                >
                  {createVoicePending ? 'Saving…' : (createVoiceTab === 'random' ? 'Use this voice' : 'Clone voice')}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default React.memo(ImageGenView);
